import { useState, useEffect, useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { SegmentedControl, TabBar, type TabItem } from '../../components/ui/Tabs';
import { Pagination, usePagination } from '../../components/ui/Pagination';
import { ChevronDown, ChevronRight, Trash2 } from 'lucide-react';
import { RANGE_PARAM, humanize } from '../Marketing/shared';
import { OpportunitiesView } from '../Dashboard/gtm/OpportunitiesView';
import { Card, StatTile } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input, Select, Textarea } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/States';
import { Alert } from '../../components/ui/Alert';
import { Modal } from '../../components/ui/Modal';
import {
  useContentItems,
  useContentLibrarySummary,
  useContentGenerate,
  useResearch,
  useContentItem,
  useDeleteContentItem,
  usePatchContentItem,
  useApproveContentItem,
  useExportToSanity,
  useSanityTypes,
  useContentVersions,
  useBulkGenerate,
  useImportWebsiteUrls,
  useRewriteNotionSources,
  useSanityDocumentTypes,
  useDuplicateSanityStructure,
  useGenerateFromStructure,
  useReopenWebsiteReview,
  useGenerateWebsiteRefresh,
  useRestoreContentVersion,
} from '../../hooks/useContent';
import type { DuplicateSanityStructureResponse, SanityDocumentTypeInfo } from '../../api/content';
import {
  useFeedbackRules,
  useCreateFeedbackRule,
  usePatchFeedbackRule,
  useDeleteFeedbackRule,
  usePendingFeedbackRules,
  useRuleLibraryPreview,
  useApplyRuleToLibrary,
} from '../../hooks/useFeedbackRules';
import type { RuleApplicationResult, RuleLibraryCandidate } from '../../api/feedbackRules';
import { useAuth } from '../../hooks/useAuth';
import { ContentItemCard } from './ContentItemCard';
import { RULE_NEAR_DUPLICATE, findRuleIssues, groupByTitle, issueRuleIds, issuesPerRule, ruleWords, wordSimilarity, type RuleIssue } from './content-duplicates';
import { RegenerateDialog } from './RegenerateDialog';
import { ImpactArticleDialog } from './ImpactArticleDialog';
import { VoiceSamplesModal } from './VoiceSamplesModal';
import { ResearchPreview } from './ResearchPreview';
import { RewriteTab } from './RewriteTab';
import { WebsiteReviewPanel } from './WebsiteReviewPanel';
import type {
  ContentItem,
  ContentVersion,
  ContentItemFilters,
  ResearchBrief,
  GenerateInput,
  BulkGenerateResponse,
  NotionBriefSource,
  LibrarySummary,
  WebsiteImportResponse,
  VoicePersona,
} from '../../api/content';
import { getContentItem } from '../../api/content';
import type { ContentType, ContentLanguage, IcpSegment } from '@sequesto-os/shared';
import {
  CONTENT_LANGUAGES,
  ICP_SEGMENTS,
} from '@sequesto-os/shared';
import { ICP_LABELS, TYPE_LABELS } from './content-labels';
import { CONTENT_TYPE_FIELDS, getDefaultExtraFields } from './content-type-fields';
import type { ExtraFieldConfig } from './content-type-fields';
import { type ReviewMeta } from './reviewers';
import { ReviewerPicker } from './ReviewerPicker';
import { getDisplayStatusLabel, getDisplayStatusVariant, isImportedPost, STALE_APPROVAL_DAYS, staleApprovalDays } from './content-status';
import { isConstant } from '../../components/ui/Table';
import { formatDayMonthYearTime, formatDayMonthYear } from '../../utils/dateFormat';
type SubTab = 'website' | 'linkedin' | 'opportunities' | 'feedback';
type WebsiteIntent = 'create' | 'optimize' | 'import';

const LONG_FORM_TYPES: ContentType[] = [
  'seo_article', 'comparison_page', 'faq_page', 'industry_page',
  'use_case_page', 'case_study', 'glossary_entry', 'landing_page',
  'integration_page', 'solution_page', 'feature_page',
];

const CONTENT_ENGINE_ROUTES: Record<SubTab, string> = {
  website: '/marketing/content/website',
  linkedin: '/marketing/content/linkedin',
  opportunities: '/marketing/content/opportunities',
  feedback: '/marketing/content/generation-rules',
};

/** Old sub-paths that folded into another view: the library now sits under Website and LinkedIn, the calendar under Opportunities. */
const LEGACY_CONTENT_ROUTES: Record<string, string> = {
  '/marketing/content/library': CONTENT_ENGINE_ROUTES.website,
  '/marketing/content/calendar': CONTENT_ENGINE_ROUTES.opportunities,
};

/** Names a sub-tab can go by in a path segment or a `?tab=` / `?view=` / `?section=` deep link. */
const SUB_TAB_ALIASES: Record<string, SubTab> = {
  website: 'website',
  linkedin: 'linkedin',
  opportunities: 'opportunities',
  rules: 'feedback',
  feedback: 'feedback',
  'generation-rules': 'feedback',
};

function getSubTab(pathname: string, search: URLSearchParams): SubTab {
  if (pathname.startsWith(CONTENT_ENGINE_ROUTES.linkedin)) return 'linkedin';
  if (pathname.startsWith(CONTENT_ENGINE_ROUTES.opportunities)) return 'opportunities';
  if (pathname.startsWith(CONTENT_ENGINE_ROUTES.feedback)) return 'feedback';
  // `/marketing/content/rules` and `/marketing/content?tab=rules` used to fall through to Website.
  const segment = pathname.replace(/\/$/, '').split('/')[3];
  if (segment && SUB_TAB_ALIASES[segment]) return SUB_TAB_ALIASES[segment];
  for (const key of ['tab', 'view', 'section']) {
    const alias = search.get(key);
    if (alias && SUB_TAB_ALIASES[alias]) return SUB_TAB_ALIASES[alias];
  }
  return 'website';
}

const SUB_TABS: readonly TabItem<SubTab>[] = [
  { id: 'website', label: 'Website' },
  { id: 'linkedin', label: 'LinkedIn' },
  { id: 'opportunities', label: 'Opportunities' },
  { id: 'feedback', label: 'Rules' },
];

/** Scrolls to the library list rendered under the Website and LinkedIn views. */
function scrollToLibrary() {
  document.getElementById('content-library')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export function ContentDashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const subTab = getSubTab(location.pathname, searchParams);
  const openRewriteProjectId = searchParams.get('project');

  function clearOpenRewriteProject() {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('project');
        return next;
      },
      { replace: true },
    );
  }

  function openRewriteProject(id: string) {
    navigate(`${CONTENT_ENGINE_ROUTES.website}?project=${encodeURIComponent(id)}`);
    window.scrollTo({ top: 0 });
  }

  // The library's status tiles lead the page (the answer first); a tile opens
  // those items in the library below, which remounts on each request.
  const summary = useContentLibrarySummary();
  const [libraryRequest, setLibraryRequest] = useState<{ n: number; group: LibraryGroup; status: StatusFilter | '' } | null>(null);
  function openLibrary(group: LibraryGroup, status: StatusFilter | '' = '') {
    setLibraryRequest((prev) => ({ n: (prev?.n ?? 0) + 1, group, status }));
    window.setTimeout(scrollToLibrary, 0);
  }
  const statusRow = (channel: 'website' | 'linkedin') =>
    summary.data ? (
      <LibraryStatusRow
        summary={summary.data}
        channel={channel}
        onStatus={(st) => openLibrary({ channel, key: 'all' } as LibraryGroup, st)}
        onLive={() => openLibrary({ channel: 'website', key: 'live' })}
      />
    ) : null;
  const libraryFor = (channel: 'website' | 'linkedin') => (
    <LibraryTab
      key={`${channel}-${libraryRequest?.n ?? 0}`}
      channel={channel}
      onOpenRewriteProject={openRewriteProject}
      initialGroup={libraryRequest?.group.channel === channel ? libraryRequest.group : null}
      initialStatus={libraryRequest?.group.channel === channel ? libraryRequest.status : ''}
    />
  );

  const legacyTarget = Object.entries(LEGACY_CONTENT_ROUTES).find(([from]) => location.pathname.replace(/\/$/, '') === from)?.[1];
  if (legacyTarget) {
    return <Navigate to={`${legacyTarget}${location.search}`} replace />;
  }

  return (
    <div className="space-y-6">
      <TabBar
        tabs={SUB_TABS}
        active={subTab}
        onSelect={(id) => {
          // The Marketing period is shared across tabs, so it rides along.
          const range = searchParams.get(RANGE_PARAM);
          navigate(`${CONTENT_ENGINE_ROUTES[id]}${range ? `?${RANGE_PARAM}=${encodeURIComponent(range)}` : ''}`);
        }}
        size="sm"
        breadcrumb={false}
      />

      {subTab === 'website' && (
        <>
          {statusRow('website')}
          <WebsiteTab
            initialProjectId={openRewriteProjectId}
            onInitialProjectLoaded={clearOpenRewriteProject}
          />
          {libraryFor('website')}
        </>
      )}
      {subTab === 'linkedin' && (
        <>
          {statusRow('linkedin')}
          <GenerateTab />
          {libraryFor('linkedin')}
        </>
      )}
      {subTab === 'opportunities' && <OpportunitiesView />}
      {subTab === 'feedback' && <FeedbackTab />}
    </div>
  );
}

function WebsiteIntentSelector({
  value,
  onChange,
}: {
  value: WebsiteIntent | null;
  onChange: (intent: WebsiteIntent) => void;
}) {
  const options: Array<{
    id: WebsiteIntent;
    title: string;
    description: string;
  }> = [
    {
      id: 'create',
      title: 'Create a new page',
      description:
        'Start from the planning rows in Notion, generate a fresh draft, then review it before publishing.',
    },
    {
      id: 'optimize',
      title: 'Improve a live page',
      description:
        'Paste one or more live URLs, generate a stronger version from the current page plus your strategy databases, then review it.',
    },
    {
      id: 'import',
      title: 'Import a live page to edit',
      description:
        'Paste one or more live URLs, pull the current Sanity fields into the OS, copy current content into the proposed draft, then edit it yourself.',
    },
  ];

  return (
    <Card className="p-4">
      {/* Collapsed to one row until a job is chosen; the job's description appears once it is. */}
      <div className="flex flex-wrap items-center gap-3">
        <h3 className="text-body-md font-medium text-text-primary">What do you want to do?</h3>
        <SegmentedControl
          ariaLabel="Website job"
          options={options.map((o) => ({ id: o.id, label: o.title }))}
          value={value ?? ('' as WebsiteIntent)}
          onChange={onChange}
        />
      </div>
      {value && (
        <p className="mt-3 text-body-sm text-text-secondary">{options.find((o) => o.id === value)?.description}</p>
      )}
    </Card>
  );
}

function WebsiteStepSection({
  step,
  title,
  description,
  children,
}: {
  step: number;
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-3">
      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-surface-secondary text-body-sm font-semibold text-text-primary">
          {step}
        </div>
        <div className="space-y-1">
          <h3 className="text-body-lg font-medium text-text-primary">{title}</h3>
          <p className="text-body-sm text-text-secondary">{description}</p>
        </div>
      </div>
      {children}
    </section>
  );
}

/* ─── Generate Tab ─── */

export function GenerateTab() {
  const contentType: ContentType = 'linkedin_post';
  const [topic, setTopic] = useState('');
  const [notes, setNotes] = useState('');
  const [language, setLanguage] = useState<ContentLanguage>('en');
  const [icpSegments, setIcpSegments] = useState<IcpSegment[]>([]);
  const [useResearchFlag, setUseResearchFlag] = useState(true);
  const [useSerpAnalysis, setUseSerpAnalysis] = useState(false);
  const [serpTargetKeyword, setSerpTargetKeyword] = useState('');
  const [serpMaxIterations, setSerpMaxIterations] = useState(2);
  const [serpTargetScore, setSerpTargetScore] = useState(0.75);
  const [persona, setPersona] = useState<'patrick' | 'evrard' | 'sequesto'>('patrick');
  const [extraFields, setExtraFields] = useState<Record<string, unknown>>(() => getDefaultExtraFields('linkedin_post'));

  const typeFields = CONTENT_TYPE_FIELDS[contentType];

  function setExtraField(key: string, value: unknown) {
    setExtraFields((prev) => ({ ...prev, [key]: value }));
  }

  const generate = useContentGenerate();
  const research = useResearch();
  const [researchBrief, setResearchBrief] = useState<ResearchBrief | null>(null);
  const [generatedItemId, setGeneratedItemId] = useState<string | null>(null);

  const generatedItem = useContentItem(generatedItemId);

  // Check if all required extra fields are filled
  const requiredFieldsMissing = typeFields?.some(
    (f) => f.required && !extraFields[f.key],
  ) ?? false;

  async function handleResearch() {
    if (!topic.trim()) return;
    const brief = await research.mutateAsync({
      topic: topic.trim(),
      language,
      contentType,
      icpSegments: icpSegments.length > 0 ? icpSegments : undefined,
    });
    setResearchBrief(brief);
  }

  async function handleGenerate() {
    if (!topic.trim() || requiredFieldsMissing) return;

    const input: GenerateInput = {
      contentType,
      topic: topic.trim(),
      notes: notes.trim() || undefined,
      language,
      useResearch: useResearchFlag,
      useSerpAnalysis: useSerpAnalysis && isLongForm,
      serpTargetKeyword: useSerpAnalysis && serpTargetKeyword.trim() ? serpTargetKeyword.trim() : undefined,
      serpTargetScore: useSerpAnalysis ? serpTargetScore : undefined,
      serpMaxIterations: useSerpAnalysis ? serpMaxIterations : undefined,
      icp: icpSegments.length > 0 ? { segments: icpSegments } : undefined,
      extra: {
        ...extraFields,
      },
    };

    if (contentType === 'linkedin_post') {
      input.persona = persona;
    }

    try {
      const result = await generate.mutateAsync(input) as Record<string, unknown>;

      // For long-form content, track the background job
      if ('contentItemId' in result) {
        setGeneratedItemId(result.contentItemId as string);
      }
    } catch {
      // Error is already captured by React Query (generate.isError) — nothing extra needed
    }
  }

  const isLinkedIn = contentType === 'linkedin_post';
  const isLongForm = LONG_FORM_TYPES.includes(contentType);

  return (
    <div className="space-y-6">
      <Card className="p-6 space-y-4">
        <h3 className="text-body-md font-medium text-text-primary">Generate a LinkedIn post</h3>

        {/* Topic */}
        <div>
          <label className="block text-body-xs font-medium text-text-secondary mb-1">Topic</label>
          <Input
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder={isLinkedIn ? 'What should the post be about?' : 'Article topic or primary keyword...'}
          />
        </div>

        {/* Notes */}
        <div>
          <label className="block text-body-xs font-medium text-text-secondary mb-1">Notes (optional)</label>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add context, key points, or specific angles..."
            rows={3}
            className="resize-y"
          />
        </div>

        {/* Language + persona */}
        <div className="grid max-w-xl grid-cols-2 gap-4">
          <div>
            <label className="block text-body-xs font-medium text-text-secondary mb-1">Language</label>
            <Select
              value={language}
              onChange={(e) => setLanguage(e.target.value as ContentLanguage)}
              className="w-full"
            >
              {CONTENT_LANGUAGES.map((l) => (
                <option key={l} value={l}>{l.toUpperCase()}</option>
              ))}
            </Select>
          </div>
          {isLinkedIn && (
            <div>
              <label className="block text-body-xs font-medium text-text-secondary mb-1">Persona</label>
              <Select
                value={persona}
                onChange={(e) => setPersona(e.target.value as 'patrick' | 'evrard' | 'sequesto')}
                className="w-full"
              >
                <option value="patrick">Patrick (CEO)</option>
                <option value="evrard">Evrard (CTO)</option>
                <option value="sequesto">SEQUESTO</option>
              </Select>
            </div>
          )}
        </div>

        <div>
            <label className="block text-body-xs font-medium text-text-secondary mb-1">ICP segments</label>
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              {ICP_SEGMENTS.filter((s) => s !== 'general').map((s) => (
                <label key={s} className="flex items-center gap-1.5 text-body-xs text-text-secondary cursor-pointer">
                  <input
                    type="checkbox"
                    checked={icpSegments.includes(s)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setIcpSegments((prev) => [...prev, s]);
                      } else {
                        setIcpSegments((prev) => prev.filter((x) => x !== s));
                      }
                    }}
                    className="h-4 w-4 cursor-pointer rounded-sm border-border"
                  />
                  {ICP_LABELS[s] ?? s}
                </label>
              ))}
            </div>
          </div>

        {/* SERP Analysis Options */}
        {isLongForm && useSerpAnalysis && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 border-t border-border pt-4">
            <div>
              <label className="block text-body-xs font-medium text-text-secondary mb-1">
                SERP Target Keyword <span className="text-text-tertiary">(optional)</span>
              </label>
              <Input
                value={serpTargetKeyword}
                onChange={(e) => setSerpTargetKeyword(e.target.value)}
                placeholder="Defaults to topic"
              />
            </div>
            <div>
              <label className="block text-body-xs font-medium text-text-secondary mb-1">Entity Revision Iterations</label>
              <Select
                value={serpMaxIterations}
                onChange={(e) => setSerpMaxIterations(Number(e.target.value))}
                className="w-full"
              >
                <option value={0}>0: Skip entity revision</option>
                <option value={1}>1: Single pass</option>
                <option value={2}>2: Standard (default)</option>
                <option value={3}>3: Thorough</option>
              </Select>
            </div>
            <div>
              <label className="block text-body-xs font-medium text-text-secondary mb-1">Target Score</label>
              <Select
                value={serpTargetScore}
                onChange={(e) => setSerpTargetScore(Number(e.target.value))}
                className="w-full"
              >
                <option value={0.6}>0.60: Relaxed</option>
                <option value={0.7}>0.70: Moderate</option>
                <option value={0.75}>0.75: Standard (default)</option>
                <option value={0.8}>0.80: Strict</option>
                <option value={0.9}>0.90: Very strict</option>
              </Select>
            </div>
          </div>
        )}

        {/* Type-specific fields */}
        {typeFields && typeFields.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-border pt-4">
            {typeFields.map((field) => (
              <ExtraField
                key={field.key}
                config={field}
                value={extraFields[field.key] ?? ''}
                onChange={(v) => setExtraField(field.key, v)}
              />
            ))}
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col gap-2 pt-2">
          <div className="flex flex-wrap items-center gap-3">
            <Button
              onClick={handleGenerate}
              disabled={!topic.trim() || requiredFieldsMissing || generate.isPending}
            >
              {generate.isPending ? 'Generating...' : isLongForm ? 'Generate (Background)' : 'Generate'}
            </Button>
            {generate.isPending && (
              <Button variant="secondary" onClick={() => generate.reset()}>
                Cancel
              </Button>
            )}
            {useResearchFlag && (
              <Button
                variant="secondary"
                onClick={handleResearch}
                disabled={!topic.trim() || research.isPending}
              >
                {research.isPending ? 'Researching...' : 'Preview research'}
              </Button>
            )}
            <label className="flex items-center gap-2 text-body-xs text-text-secondary cursor-pointer">
              <input
                type="checkbox"
                checked={useResearchFlag}
                onChange={(e) => setUseResearchFlag(e.target.checked)}
                className="h-4 w-4 cursor-pointer rounded-sm border-border"
              />
              Use research
            </label>
            {isLongForm && (
              <label className="flex items-center gap-2 text-body-xs text-text-secondary cursor-pointer">
                <input
                  type="checkbox"
                  checked={useSerpAnalysis}
                  onChange={(e) => setUseSerpAnalysis(e.target.checked)}
                  className="h-4 w-4 cursor-pointer rounded-sm border-border"
                />
                SERP analysis
              </label>
            )}
          </div>
          {!topic.trim() && (
            <p className="text-body-xs text-text-tertiary">Enter a topic above to enable generation.</p>
          )}
          {topic.trim() && requiredFieldsMissing && (
            <p className="text-body-xs text-error">Fill in all required fields (*) above.</p>
          )}
        </div>

        {generate.isError && (
          <Alert variant="error">{generate.error instanceof Error ? generate.error.message : 'Generation failed'}</Alert>
        )}
        {research.isError && (
          <Alert variant="error">{research.error instanceof Error ? research.error.message : 'Research failed'}</Alert>
        )}
      </Card>

      {/* Research Preview */}
      {researchBrief && <ResearchPreview brief={researchBrief} />}

      {/* Generation Result (for long-form content being generated) */}
      {generatedItemId && generatedItem.data && (
        <Card className="p-4 space-y-3">
          <div className="flex items-center gap-2">
            <h3 className="text-body-sm font-medium text-text-primary">Generation Status</h3>
            {(generatedItem.data.metadata as Record<string, unknown>)?.generating ? (
              <Spinner className="w-4 h-4" />
            ) : (
              <Badge variant="success">Complete</Badge>
            )}
          </div>
          {Boolean((generatedItem.data.metadata as Record<string, unknown>)?.generating) && !((generatedItem.data.metadata as Record<string, unknown>)?.error) && (
            <GenerationProgress
              phase={(generatedItem.data.metadata as Record<string, unknown>)?.phase as string | undefined}
              startedAt={generatedItem.data.createdAt}
            />
          )}
          {(generatedItem.data.metadata as Record<string, unknown>)?.error ? (
            <Alert variant="error">
              {String((generatedItem.data.metadata as Record<string, unknown>).error)}
            </Alert>
          ) : generatedItem.data.body ? (
            <div className="text-body-xs text-text-secondary max-h-96 overflow-y-auto whitespace-pre-wrap">
              {generatedItem.data.body}
            </div>
          ) : null}
        </Card>
      )}

      {/* LinkedIn sync result */}
      {generate.data && !generatedItemId && (
        <LinkedInResult data={generate.data as Record<string, unknown>} />
      )}
    </div>
  );
}

function WebsiteTab({
  initialProjectId,
  onInitialProjectLoaded,
}: {
  initialProjectId?: string | null;
  onInitialProjectLoaded?: () => void;
}) {
  const navigate = useNavigate();
  const notionSourcesQuery = useRewriteNotionSources();
  const generateCreateDrafts = useBulkGenerate();
  const duplicateStructure = useDuplicateSanityStructure();
  const generateFromStructure = useGenerateFromStructure();
  const [intent, setIntent] = useState<WebsiteIntent | null>(initialProjectId ? 'optimize' : null);
  const [createSelectedSanityType, setCreateSelectedSanityType] = useState<string>('');
  const [createDuplicateResult, setCreateDuplicateResult] = useState<DuplicateSanityStructureResponse | null>(null);
  const [selectedNotionSourceIds, setSelectedNotionSourceIds] = useState<Set<string>>(new Set());
  const [customNotionSourcesText, setCustomNotionSourcesText] = useState('');
  const [notionSourceDefaultsApplied, setNotionSourceDefaultsApplied] = useState(false);
  const [createUseResearch, setCreateUseResearch] = useState(true);
  const [createUseSerpAnalysis, setCreateUseSerpAnalysis] = useState(true);
  const [createStartFromScratch, setCreateStartFromScratch] = useState(true);
  const [createRunInstructions, setCreateRunInstructions] = useState('');
  const [createGenerationResult, setCreateGenerationResult] = useState<BulkGenerateResponse | null>(null);
  const [createPageName, setCreatePageName] = useState('');
  const [createSeoKeyword, setCreateSeoKeyword] = useState('');
  const [createCompetitor, setCreateCompetitor] = useState('');
  const [createDescription, setCreateDescription] = useState('');
  const [createFromStructureSubmitted, setCreateFromStructureSubmitted] = useState(false);
  const [optimizeUrlsText, setOptimizeUrlsText] = useState('');
  const [importUrlsText, setImportUrlsText] = useState('');
  const [optimizeUseResearch, setOptimizeUseResearch] = useState(true);
  const [optimizeUseSerpAnalysis, setOptimizeUseSerpAnalysis] = useState(true);
  const [optimizeStartFromScratch, setOptimizeStartFromScratch] = useState(false);
  const [optimizeRunInstructions, setOptimizeRunInstructions] = useState('');
  const lockedNotionSourceIds = useMemo(() => new Set<string>(['auto-website', 'positioning']), []);

  const selectedNotionDatabaseIds = notionSourcesQuery.data
    ? [
        ...new Set([
          'auto-website',
          'positioning',
          ...Array.from(selectedNotionSourceIds),
          ...parseCustomNotionDatabaseList(customNotionSourcesText),
        ]),
      ]
    : [...new Set(['auto-website', 'positioning', ...parseCustomNotionDatabaseList(customNotionSourcesText)])];

  useEffect(() => {
    if (!notionSourcesQuery.data || notionSourceDefaultsApplied) return;
    const defaults = new Set(
      notionSourcesQuery.data.sources
        .filter((source) => source.defaultOn)
        .map((source) => source.id),
    );
    setSelectedNotionSourceIds(defaults);
    setNotionSourceDefaultsApplied(true);
  }, [notionSourceDefaultsApplied, notionSourcesQuery.data]);

  useEffect(() => {
    if (!initialProjectId) return;
    setIntent('optimize');
  }, [initialProjectId]);

  useEffect(() => {
    if (!createUseResearch) {
      setCreateUseSerpAnalysis(false);
    }
  }, [createUseResearch]);

  function openContentReviewItem(id: string) {
    navigate(`/marketing/content/library/${id}`, { state: { fromLibrary: true } });
  }

  function toggleNotionSource(id: string) {
    if (lockedNotionSourceIds.has(id)) return;
    setSelectedNotionSourceIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const createWorkflowBusy =
    generateCreateDrafts.isPending || duplicateStructure.isPending || generateFromStructure.isPending;

  // Step 1's Sanity type drives the bulk-generate flow — language defaults to
  // English and we only kick off generation for types that have a Notion
  // planning DB on the API side. Other types (webinar, glossary, author, etc.)
  // get the structure clone but skip the LLM-fill path.
  const derivedCreatePageType: ContentType | undefined =
    SANITY_TYPE_TO_CONTENT_TYPE[createSelectedSanityType];

  const requireCompetitor = derivedCreatePageType === 'comparison_page';

  async function handleCreateBlankDraftFromStructure() {
    if (!createSelectedSanityType) return;
    const result = await duplicateStructure.mutateAsync(createSelectedSanityType);
    setCreateDuplicateResult(result);
    setCreateFromStructureSubmitted(false);
  }

  async function handleGenerateCreateDrafts() {
    if (!derivedCreatePageType) return;

    if (createDuplicateResult) {
      if (!createPageName.trim()) return;
      if (requireCompetitor && !createCompetitor.trim()) return;
      await generateFromStructure.mutateAsync({
        contentItemId: createDuplicateResult.contentItemId,
        input: {
          pageName: createPageName.trim(),
          ...(createSeoKeyword.trim() ? { seoKeyword: createSeoKeyword.trim() } : {}),
          ...(createCompetitor.trim() ? { competitor: createCompetitor.trim() } : {}),
          ...(createDescription.trim() ? { description: createDescription.trim() } : {}),
          ...(createRunInstructions.trim() ? { runInstructions: createRunInstructions.trim() } : {}),
          language: 'en',
          useResearch: createUseResearch,
          useSerpAnalysis: createUseSerpAnalysis,
          notionDatabaseIds: selectedNotionDatabaseIds,
        },
      });
      setCreateFromStructureSubmitted(true);
      return;
    }

    const result = await generateCreateDrafts.mutateAsync({
      pageType: derivedCreatePageType,
      language: 'en',
      limit: 1,
      dryRun: false,
      useResearch: createUseResearch,
      useSerpAnalysis: createUseSerpAnalysis,
      extra: {
        notionDatabaseIds: selectedNotionDatabaseIds,
        startFromScratch: createStartFromScratch,
        ...(createRunInstructions.trim() ? { runInstructions: createRunInstructions.trim() } : {}),
      },
    });
    setCreateGenerationResult(result);
  }

  return (
    <div className="space-y-6">
      <WebsiteIntentSelector
        value={intent}
        onChange={(nextIntent) => {
          setIntent(nextIntent);
          onInitialProjectLoaded?.();
        }}
      />

      {intent === 'create' && (
        <WebsiteStepSection
          step={1}
          title="Pick the page structure"
          description="Choose an existing Sanity page type: we’ll duplicate its structure into a fresh empty draft."
        >
          <WebsiteCreateStructureSourceCard
            selectedSanityType={createSelectedSanityType}
            onSelectedSanityTypeChange={setCreateSelectedSanityType}
            onCreateBlankDraft={handleCreateBlankDraftFromStructure}
            isCreating={duplicateStructure.isPending}
            createError={
              duplicateStructure.isError
                ? duplicateStructure.error instanceof Error
                  ? duplicateStructure.error.message
                  : 'Failed to create blank draft'
                : null
            }
            createResult={createDuplicateResult}
          />
        </WebsiteStepSection>
      )}

      {intent === 'optimize' && (
        <>
          <WebsiteStepSection
            step={1}
            title="Select the live page to improve"
            description="Paste one or more public website URLs so we know exactly which live page should be refreshed."
          >
            <WebsiteUrlSelectionCard
              urlsText={optimizeUrlsText}
              onUrlsTextChange={setOptimizeUrlsText}
              mode="refresh"
            />
          </WebsiteStepSection>

          <WebsiteStepSection
            step={2}
            title="Configuration"
            description="Choose the context, generation options, and reusable learnings that should influence the refreshed copy."
          >
            <NotionSourceSelectorCard
              sources={notionSourcesQuery.data?.sources ?? []}
              isLoading={notionSourcesQuery.isLoading}
              selectedSourceIds={selectedNotionSourceIds}
              onToggle={toggleNotionSource}
              lockedSourceIds={lockedNotionSourceIds}
              customSourcesText={customNotionSourcesText}
              onCustomSourcesTextChange={setCustomNotionSourcesText}
              description="These selected databases will guide the refresh prompt for the live page update. Add extra Notion database URLs or IDs below if this page needs more context."
            />
            <WebsiteGenerationOptionsCard
              useResearch={optimizeUseResearch}
              onUseResearchChange={(checked) => {
                setOptimizeUseResearch(checked);
                if (!checked) setOptimizeUseSerpAnalysis(false);
              }}
              useSerpAnalysis={optimizeUseSerpAnalysis}
              onUseSerpAnalysisChange={setOptimizeUseSerpAnalysis}
              startFromScratch={optimizeStartFromScratch}
              onStartFromScratchChange={setOptimizeStartFromScratch}
            />
            <WebsiteRunInstructionsCard
              value={optimizeRunInstructions}
              onChange={setOptimizeRunInstructions}
            />
            <WebsiteRefreshGenerationCard
              urlsText={optimizeUrlsText}
              onGenerationStart={() => undefined}
              onGoToLibrary={scrollToLibrary}
              selectedNotionDatabaseIds={selectedNotionDatabaseIds}
              useResearch={optimizeUseResearch}
              useSerpAnalysis={optimizeUseSerpAnalysis}
              startFromScratch={optimizeStartFromScratch}
              runInstructions={optimizeRunInstructions.trim() || undefined}
              isGenerating={false}
            />
          </WebsiteStepSection>
        </>
      )}

      {intent === 'import' && (
        <>
          <WebsiteStepSection
            step={1}
            title="Select the live page to import"
            description="Paste one or more public website URLs so we can import the current Sanity-backed page fields into the editor."
          >
            <WebsiteUrlSelectionCard
              urlsText={importUrlsText}
              onUrlsTextChange={setImportUrlsText}
              mode="import"
            />
          </WebsiteStepSection>

          <WebsiteStepSection
            step={2}
            title="Import current content"
            description="We’ll create a review row from the live Sanity snapshot, copy the current content into the proposed version, and open it for manual edits."
          >
            <WebsiteManualImportCard
              urlsText={importUrlsText}
              onOpenImportedItem={openContentReviewItem}
              onGoToLibrary={scrollToLibrary}
            />
          </WebsiteStepSection>
        </>
      )}

      {intent === 'create' && (
        <>
          <WebsiteStepSection
            step={2}
            title="Configuration"
            description="Choose the Notion context, generation options, and learned guidance that should shape the new website draft."
          >
            {createDuplicateResult && (
              <WebsiteCreateBriefFieldsCard
                pageName={createPageName}
                onPageNameChange={setCreatePageName}
                seoKeyword={createSeoKeyword}
                onSeoKeywordChange={setCreateSeoKeyword}
                competitor={createCompetitor}
                onCompetitorChange={setCreateCompetitor}
                description={createDescription}
                onDescriptionChange={setCreateDescription}
                requireCompetitor={requireCompetitor}
                pageType={derivedCreatePageType}
              />
            )}
            <NotionSourceSelectorCard
              sources={notionSourcesQuery.data?.sources ?? []}
              isLoading={notionSourcesQuery.isLoading}
              selectedSourceIds={selectedNotionSourceIds}
              onToggle={toggleNotionSource}
              lockedSourceIds={lockedNotionSourceIds}
              customSourcesText={customNotionSourcesText}
              onCustomSourcesTextChange={setCustomNotionSourcesText}
              description="These selected databases will guide the new page draft. Keep them all on unless you intentionally want a narrower brief."
            />
            <WebsiteGenerationOptionsCard
              useResearch={createUseResearch}
              onUseResearchChange={(checked) => {
                setCreateUseResearch(checked);
                if (!checked) setCreateUseSerpAnalysis(false);
              }}
              useSerpAnalysis={createUseSerpAnalysis}
              onUseSerpAnalysisChange={setCreateUseSerpAnalysis}
              startFromScratch={createStartFromScratch}
              onStartFromScratchChange={setCreateStartFromScratch}
            />
            <WebsiteRunInstructionsCard
              value={createRunInstructions}
              onChange={setCreateRunInstructions}
            />
            <WebsiteCreateGenerationCard
              limit={1}
              pageType={derivedCreatePageType}
              selectedSanityType={createSelectedSanityType}
              generationResult={createGenerationResult}
              isGenerating={generateCreateDrafts.isPending || generateFromStructure.isPending}
              generationError={
                generateCreateDrafts.isError
                  ? generateCreateDrafts.error instanceof Error
                    ? generateCreateDrafts.error.message
                    : 'Draft generation failed'
                  : generateFromStructure.isError
                  ? generateFromStructure.error instanceof Error
                    ? generateFromStructure.error.message
                    : 'Draft generation failed'
                  : null
              }
              fromStructureSubmitted={createFromStructureSubmitted}
              fromStructureItemId={createDuplicateResult?.contentItemId ?? null}
              fromStructureGenerateDisabled={
                !!createDuplicateResult &&
                (!createPageName.trim() || (requireCompetitor && !createCompetitor.trim()))
              }
              onGenerate={handleGenerateCreateDrafts}
              onGoToLibrary={scrollToLibrary}
              disableActions={createWorkflowBusy}
            />
          </WebsiteStepSection>
        </>
      )}

      {initialProjectId && (
        <Card className="p-6 space-y-4 border-dashed">
          <div className="space-y-1">
            <h3 className="text-body-md font-medium text-text-primary">Opened legacy rewrite project</h3>
            <p className="text-body-sm text-text-secondary">
              This project was opened from the library and still uses the older rewrite workspace.
            </p>
          </div>
          <RewriteTab
            initialProjectId={initialProjectId}
            sharedNotionDatabaseIds={selectedNotionDatabaseIds}
            useSharedNotionSources
          />
        </Card>
      )}
    </div>
  );
}

function NotionSourceSelectorCard({
  sources,
  isLoading,
  selectedSourceIds,
  onToggle,
  lockedSourceIds,
  customSourcesText,
  onCustomSourcesTextChange,
  description = 'These selected databases are injected into the prompt for website refreshes, bulk page generation, and website rewrites in this tab. All sources are selected by default.',
}: {
  sources: NotionBriefSource[];
  isLoading: boolean;
  selectedSourceIds: Set<string>;
  onToggle: (id: string) => void;
  lockedSourceIds?: Set<string>;
  customSourcesText?: string;
  onCustomSourcesTextChange?: (value: string) => void;
  description?: string;
}) {
  return (
    <Card className="p-6 space-y-3">
      <div className="space-y-1">
        <h3 className="text-body-md font-medium text-text-primary">Notion Prompt Sources</h3>
        <p className="text-body-xs text-text-secondary">{description}</p>
      </div>

      {isLoading && <Spinner />}

      {!isLoading && sources.length > 0 && (
        <div className="space-y-2">
          {sources.map((source) => (
            <label key={source.id} className={`flex items-start gap-2 ${lockedSourceIds?.has(source.id) ? 'cursor-default' : 'cursor-pointer'}`}>
              <input
                type="checkbox"
                checked={selectedSourceIds.has(source.id) || lockedSourceIds?.has(source.id) === true}
                onChange={() => onToggle(source.id)}
                className="mt-1 h-4 w-4 cursor-pointer rounded-sm border-border"
                disabled={lockedSourceIds?.has(source.id)}
              />
              <div className="flex-1">
                <div className="text-body-sm text-text-primary">
                  {source.label}
                  {source.role === 'primary' && (
                    <span className="ml-2"><Badge variant="default">primary</Badge></span>
                  )}
                  {lockedSourceIds?.has(source.id) && (
                    <span className="ml-2"><Badge variant="default">required</Badge></span>
                  )}
                </div>
                <div className="text-body-xs text-text-tertiary">{source.description}</div>
              </div>
            </label>
          ))}
        </div>
      )}

      {typeof customSourcesText === 'string' && onCustomSourcesTextChange && (
        <div className="pt-2">
          <label className="block text-body-xs font-medium text-text-secondary mb-1">
            Add more Notion DB URLs or IDs
          </label>
          <Textarea
            value={customSourcesText}
            onChange={(e) => onCustomSourcesTextChange(e.target.value)}
            rows={2}
            placeholder="https://www.notion.so/... or a raw database ID, one per line"
          />
        </div>
      )}
    </Card>
  );
}

function WebsiteCreateBriefFieldsCard({
  pageName,
  onPageNameChange,
  seoKeyword,
  onSeoKeywordChange,
  competitor,
  onCompetitorChange,
  description,
  onDescriptionChange,
  requireCompetitor,
  pageType,
}: {
  pageName: string;
  onPageNameChange: (value: string) => void;
  seoKeyword: string;
  onSeoKeywordChange: (value: string) => void;
  competitor: string;
  onCompetitorChange: (value: string) => void;
  description: string;
  onDescriptionChange: (value: string) => void;
  requireCompetitor: boolean;
  pageType: ContentType | undefined;
}) {
  const typeLabel = pageType ? TYPE_LABELS[pageType] ?? pageType : 'page';
  return (
    <Card className="p-6 space-y-4">
      <div className="space-y-1">
        <h3 className="text-body-md font-medium text-text-primary">Page brief (from scratch)</h3>
        <p className="text-body-xs text-text-secondary">
          You duplicated a blank Sanity structure in step 1. Fill in the brief here: these fields replace the Notion brief for this {typeLabel}. Use the run-instructions box below for free-form direction.
        </p>
      </div>

      <div className="space-y-3">
        <div>
          <label className="block text-body-xs font-medium text-text-secondary mb-1">
            Page name <span className="text-error">*</span>
          </label>
          <Input
            value={pageName}
            onChange={(e) => onPageNameChange(e.target.value)}
            placeholder={requireCompetitor ? 'e.g. Sequesto vs Loopio' : 'e.g. RFP Response Automation for Finance'}
          />
        </div>

        <div>
          <label className="block text-body-xs font-medium text-text-secondary mb-1">SEO keyword</label>
          <Input
            value={seoKeyword}
            onChange={(e) => onSeoKeywordChange(e.target.value)}
            placeholder={requireCompetitor ? 'e.g. Loopio alternative' : 'e.g. RFP automation for banks'}
          />
        </div>

        {requireCompetitor && (
          <div>
            <label className="block text-body-xs font-medium text-text-secondary mb-1">
              Competitor <span className="text-error">*</span>
            </label>
            <Input
              value={competitor}
              onChange={(e) => onCompetitorChange(e.target.value)}
              placeholder="e.g. Loopio"
            />
          </div>
        )}

        <div>
          <label className="block text-body-xs font-medium text-text-secondary mb-1">Description / notes</label>
          <Textarea
            value={description}
            onChange={(e) => onDescriptionChange(e.target.value)}
            rows={4}
            placeholder="What this page should cover, who it targets, and the key points the generator should emphasize."
          />
        </div>
      </div>
    </Card>
  );
}

function WebsiteRunInstructionsCard({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <Card className="p-6 space-y-3">
      <div className="space-y-1">
        <h3 className="text-body-md font-medium text-text-primary">Instructions for this run</h3>
        <p className="text-body-xs text-text-secondary">
          Add any page-specific direction you want applied for this generation run only. Persistent company guidance is still applied automatically in the background.
        </p>
      </div>

      <Textarea
        rows={4}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Example: Keep the hero closer to enterprise procurement buyers. Emphasize the target keyword naturally, avoid overclaiming, and keep the title unchanged."
      />

      <p className="text-body-xs text-text-tertiary">
        These instructions are saved with this generation run for audit/history, but they do not become reusable guidance automatically.
      </p>
    </Card>
  );
}

function WebsiteGenerationOptionsCard({
  useResearch,
  onUseResearchChange,
  useSerpAnalysis,
  onUseSerpAnalysisChange,
  startFromScratch,
  onStartFromScratchChange,
}: {
  useResearch: boolean;
  onUseResearchChange: (checked: boolean) => void;
  useSerpAnalysis: boolean;
  onUseSerpAnalysisChange: (checked: boolean) => void;
  startFromScratch?: boolean;
  onStartFromScratchChange?: (checked: boolean) => void;
}) {
  return (
    <Card className="p-6 space-y-3">
      <div className="space-y-1">
        <h3 className="text-body-md font-medium text-text-primary">Generation options</h3>
        <p className="text-body-xs text-text-secondary">
          Choose how much external analysis the engine should use before it writes the draft.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <label className="flex items-center gap-2 text-body-xs text-text-secondary cursor-pointer">
          <input
            type="checkbox"
            checked={useResearch}
            onChange={(e) => onUseResearchChange(e.target.checked)}
            className="h-4 w-4 cursor-pointer rounded-sm border-border"
          />
          Use research
        </label>
        <label className="flex items-center gap-2 text-body-xs text-text-secondary cursor-pointer">
          <input
            type="checkbox"
            checked={useSerpAnalysis}
            onChange={(e) => onUseSerpAnalysisChange(e.target.checked)}
            className="h-4 w-4 cursor-pointer rounded-sm border-border"
            disabled={!useResearch}
          />
          SERP analysis
        </label>
        {typeof startFromScratch === 'boolean' && onStartFromScratchChange ? (
          <label className="flex items-center gap-2 text-body-xs text-text-secondary cursor-pointer">
            <input
              type="checkbox"
              checked={startFromScratch}
              onChange={(e) => onStartFromScratchChange(e.target.checked)}
              className="h-4 w-4 cursor-pointer rounded-sm border-border"
            />
            Start from scratch
          </label>
        ) : null}
      </div>

      <p className="text-body-xs text-text-tertiary">
        <code>Use research</code> adds external research before generation. <code>SERP analysis</code>{' '}
        adds search-result and competitor analysis on top of that, and is only available when research is enabled.
      </p>
      {typeof startFromScratch === 'boolean' ? (
        <p className="text-body-xs text-text-tertiary">
          <code>Start from scratch</code> keeps the exact Sanity page structure, but ignores the current live copy
          as writing input so the engine can rebuild the page content from the selected sources.
        </p>
      ) : null}
    </Card>
  );
}

function parseWebsiteUrlList(value: string): string[] {
  // Split on newlines only — Sanity Studio URLs contain commas in pane params,
  // so splitting on `,` would shred them into invalid fragments.
  return [...new Set(
    value
      .split(/\n+/)
      .map((entry) => entry.trim())
      .filter(Boolean),
  )];
}

function parseCustomNotionDatabaseList(value: string): string[] {
  return [...new Set(
    value
      .split(/[\n,]+/)
      .map((entry) => entry.trim())
      .filter(Boolean),
  )];
}

function humanizeSanityType(name: string, title?: string): string {
  if (title && title.trim()) return title;
  // Convert camelCase / PascalCase to Title Case ("useCasePage" → "Use Case Page")
  const spaced = name.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/[._-]+/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * Frontend reverse mapping: Sanity document type → ContentType used by the
 * bulk-generate / Notion → LLM pipeline. Mirrors the manifest on the API side
 * (page-type-manifest.ts). Returns undefined for Sanity types we don't yet
 * have a Notion-fed generation path for (webinars, glossary, authors, etc.).
 */
const SANITY_TYPE_TO_CONTENT_TYPE: Record<string, ContentType> = {
  useCasePage: 'use_case_page',
  solutionPage: 'solution_page',
  industryPage: 'industry_page',
  comparePage: 'comparison_page',
  integration: 'integration_page',
  featurePage: 'feature_page',
  productPage: 'feature_page',
  article: 'seo_article',
  glossaryTerm: 'glossary_entry',
};

function WebsiteCreateStructureSourceCard({
  selectedSanityType,
  onSelectedSanityTypeChange,
  onCreateBlankDraft,
  isCreating,
  createError,
  createResult,
}: {
  selectedSanityType: string;
  onSelectedSanityTypeChange: (next: string) => void;
  onCreateBlankDraft: () => void;
  isCreating: boolean;
  createError: string | null;
  createResult: DuplicateSanityStructureResponse | null;
}) {
  const typesQuery = useSanityDocumentTypes(true);

  const types: SanityDocumentTypeInfo[] = typesQuery.data?.types ?? [];
  const repeating = useMemo(() => types.filter((t) => !t.isSingleton), [types]);
  const singletons = useMemo(() => types.filter((t) => t.isSingleton), [types]);

  // When the picker first loads, default the selected type to the first
  // repeating type (or the first singleton if no repeating types exist).
  useEffect(() => {
    if (selectedSanityType) return;
    const first = repeating[0]?.name ?? singletons[0]?.name;
    if (first) onSelectedSanityTypeChange(first);
  }, [repeating, singletons, selectedSanityType, onSelectedSanityTypeChange]);

  return (
    <Card className="p-6 space-y-4">
      <div className="space-y-1">
        <label className="text-body-xs font-medium text-text-secondary">Structure to duplicate</label>
        {typesQuery.isLoading ? (
          <Spinner />
        ) : typesQuery.isError ? (
          <Alert variant="error">
            {typesQuery.error instanceof Error
              ? typesQuery.error.message
              : 'Failed to load Sanity document types'}
          </Alert>
        ) : (
          <Select
            className="w-full"
            value={selectedSanityType}
            onChange={(e) => onSelectedSanityTypeChange(e.target.value)}
          >
            {repeating.length > 0 && (
              <optgroup label="Content types">
                {repeating.map((t) => (
                  <option key={t.name} value={t.name}>
                    {humanizeSanityType(t.name, t.title)}
                    {t.count ? ` (${t.count})` : ''}
                  </option>
                ))}
              </optgroup>
            )}
            {singletons.length > 0 && (
              <optgroup label="Singletons & overviews">
                {singletons.map((t) => (
                  <option key={t.name} value={t.name}>
                    {humanizeSanityType(t.name, t.title)}
                  </option>
                ))}
              </optgroup>
            )}
          </Select>
        )}
        <p className="text-body-xs text-text-tertiary">
          We’ll clone the structure of the most recently updated English page of this type. All copy, images,
          and references are cleared so you can fill the draft in cleanly.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Button
          onClick={onCreateBlankDraft}
          disabled={isCreating || !selectedSanityType}
          variant="primary"
        >
          {isCreating ? 'Creating draft...' : 'Create blank draft'}
        </Button>
        <p className="text-body-xs text-text-tertiary">
          The new draft is created in Sanity and tracked in the OS Library so you can fill it via the configuration
          below or edit it directly in Sanity Studio.
        </p>
      </div>

      {createError && <Alert variant="error">{createError}</Alert>}

      {createResult && (
        <div className="space-y-2 rounded-md border border-border bg-surface-primary p-3">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="success">Draft created</Badge>
            <span className="text-body-sm text-text-primary">
              {humanizeSanityType(createResult.sanityDocumentType)}
            </span>
          </div>
          <p className="text-body-xs text-text-secondary">
            Cloned the structure of{' '}
            <span className="font-medium text-text-primary">
              {createResult.sourceTitle ?? createResult.sourceDocumentId}
            </span>
            . All content fields were cleared.
          </p>
          <div className="flex flex-wrap gap-2">
            {createResult.studioUrl && (
              <a
                href={createResult.studioUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center rounded-md border border-border-light bg-surface-secondary px-3 py-1.5 text-body-xs font-medium text-text-primary hover:bg-surface-primary"
              >
                Open draft in Sanity Studio →
              </a>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

function WebsiteUrlSelectionCard({
  urlsText,
  onUrlsTextChange,
  mode = 'refresh',
}: {
  urlsText: string;
  onUrlsTextChange: (value: string) => void;
  mode?: 'refresh' | 'import';
}) {
  const urls = parseWebsiteUrlList(urlsText);

  return (
    <Card className="p-6 space-y-4">
      <div className="space-y-1">
        <h3 className="text-body-md font-medium text-text-primary">Live page URLs</h3>
        <p className="text-body-xs text-text-secondary">
          Paste either the live website URL or the Sanity Studio link. Supported today: product
          overviews, product pages, articles, glossary terms, integrations, compare pages,
          solution pages, industry pages, and use-case pages. One URL per line.
        </p>
      </div>

      <Textarea
        value={urlsText}
        onChange={(e) => onUrlsTextChange(e.target.value)}
        rows={4}
        placeholder={`https://sequesto.com/product/sequesto-aos\nhttps://sequesto.com/solutions/ai-rfp-management\nhttps://sequesto.sanity.studio/structure/...`}
      />

      <p className="text-body-xs text-text-tertiary">
        {urls.length > 0
          ? `${urls.length} URL${urls.length > 1 ? 's' : ''} ready for ${mode === 'import' ? 'manual import' : 'refresh generation'}`
          : 'Paste one or more website or Sanity Studio URLs to continue.'}
      </p>
    </Card>
  );
}

function WebsiteManualImportCard({
  urlsText,
  onOpenImportedItem,
  onGoToLibrary,
}: {
  urlsText: string;
  onOpenImportedItem: (contentItemId: string) => void;
  onGoToLibrary: () => void;
}) {
  const importer = useImportWebsiteUrls();
  const [result, setResult] = useState<WebsiteImportResponse | null>(null);
  const urls = parseWebsiteUrlList(urlsText);

  async function handleImport() {
    if (urls.length === 0) return;
    setResult(null);
    const response = await importer.mutateAsync({
      urls,
      generateImmediately: false,
      refreshMode: 'manual',
    });
    setResult(response);
  }

  return (
    <div className="space-y-4 border-t border-border pt-4">
      <div className="space-y-1">
        <h3 className="text-body-md font-medium text-text-primary">Import without generation</h3>
        <p className="text-body-xs text-text-secondary">
          We’ll import the current Sanity-backed content into the OS, keep the current snapshot as the
          baseline, and copy it into the proposed version so you can start editing immediately.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={handleImport} disabled={urls.length === 0 || importer.isPending}>
          {importer.isPending ? 'Importing...' : 'Import for manual editing'}
        </Button>
        {urls.length > 0 && (
          <p className="text-body-xs text-text-tertiary">
            {urls.length} URL{urls.length > 1 ? 's' : ''} ready
          </p>
        )}
      </div>

      {importer.isError && (
        <Alert variant="error">
          {importer.error instanceof Error ? importer.error.message : 'Website import failed'}
        </Alert>
      )}

      {result?.failures.length ? (
        <Alert variant="error">
          {result.failures.map((failure) => `${failure.url}: ${failure.error}`).join('\n')}
        </Alert>
      ) : null}

      {result && result.imported.length > 0 && (
        <Card className="p-4 space-y-3 bg-surface-subtle">
          <div className="space-y-1">
            <h4 className="text-body-sm font-medium text-text-primary">
              {result.imported.length} page{result.imported.length === 1 ? '' : 's'} ready for manual editing
            </h4>
            <p className="text-body-xs text-text-secondary">
              Current content was imported from Sanity and copied into the proposed draft. Open a page below to start editing.
            </p>
          </div>

          <div className="space-y-2">
            {result.imported.map((entry) => (
              <div
                key={entry.contentItemId}
                className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-surface-primary px-4 py-3"
              >
                <div className="space-y-1 min-w-0">
                  <div className="text-body-sm font-medium text-text-primary">{entry.title}</div>
                  <div className="text-body-xs text-text-tertiary break-all">{entry.url}</div>
                  <div className="text-body-xs text-text-secondary">
                    {entry.created ? 'Imported as a new manual-edit row' : 'Refreshed the existing manual-edit row'}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="success">Ready</Badge>
                  <Button size="sm" variant="secondary" onClick={() => onOpenImportedItem(entry.contentItemId)}>
                    Open editor
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {result.imported.length > 1 && (
            <Button size="sm" variant="secondary" onClick={onGoToLibrary}>
              Go to Library
            </Button>
          )}
        </Card>
      )}
    </div>
  );
}

function WebsiteRefreshGenerationCard({
  onGenerationStart,
  onGoToLibrary,
  selectedNotionDatabaseIds,
  urlsText,
  useResearch,
  useSerpAnalysis,
  startFromScratch,
  runInstructions,
  isGenerating,
}: {
  onGenerationStart: () => void;
  onGoToLibrary: () => void;
  selectedNotionDatabaseIds?: string[];
  urlsText: string;
  useResearch: boolean;
  useSerpAnalysis: boolean;
  startFromScratch: boolean;
  runInstructions?: string;
  isGenerating: boolean;
}) {
  const importer = useImportWebsiteUrls();
  const [result, setResult] = useState<WebsiteImportResponse | null>(null);
  const [trackedEntries, setTrackedEntries] = useState<WebsiteImportResponse['imported']>([]);

  const urls = parseWebsiteUrlList(urlsText);
  const trackedItemQueries = useQueries({
    queries: trackedEntries.map((entry) => ({
      queryKey: ['content', 'item', entry.contentItemId],
      queryFn: () => getContentItem(entry.contentItemId),
      refetchInterval: (query: { state: { data?: ContentItem } }) => {
        const data = query.state.data;
        if (data && (data.metadata as Record<string, unknown>)?.generating) {
          return 3000;
        }
        return false;
      },
      staleTime: 0,
    })),
  });

  const trackedItems = trackedEntries.map((entry, index) => ({
    entry,
    query: trackedItemQueries[index],
    meta: ((trackedItemQueries[index]?.data?.metadata as Record<string, unknown> | undefined) ?? {}),
  }));
  const anyTrackedGenerating = trackedItems.some(({ query, meta }) => query?.isLoading || Boolean(meta.generating));
  const readyTrackedItems = trackedItems.filter(({ query, meta }) => query?.data && !meta.generating && !meta.error);
  const failedTrackedItems = trackedItems.filter(({ meta }) => typeof meta.error === 'string' && !meta.generating);

  async function handleImport() {
    if (urls.length === 0) return;
    onGenerationStart();
    setResult(null);
    setTrackedEntries([]);
    const response = await importer.mutateAsync({
      urls,
      generateImmediately: true,
      useResearch,
      useSerpAnalysis,
      startFromScratch,
      notionDatabaseIds: selectedNotionDatabaseIds,
      runInstructions,
    });
    setResult(response);
    setTrackedEntries(response.imported);
  }

  return (
    <div className="space-y-4 border-t border-border pt-4">
      <div className="space-y-1">
        <h3 className="text-body-md font-medium text-text-primary">Generate content</h3>
        <p className="text-body-xs text-text-secondary">
          When you’re ready, we’ll import the selected page into the OS and generate the refreshed draft
          using the configuration above.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={handleImport} disabled={urls.length === 0 || importer.isPending || isGenerating || anyTrackedGenerating}>
          {importer.isPending || isGenerating || anyTrackedGenerating
            ? 'Generating...'
            : 'Generate content'}
        </Button>
        {urls.length > 0 && (
          <p className="text-body-xs text-text-tertiary">
            {urls.length} URL{urls.length > 1 ? 's' : ''} ready
          </p>
        )}
      </div>

      {importer.isError && (
        <Alert variant="error">
          {importer.error instanceof Error ? importer.error.message : 'Website import failed'}
        </Alert>
      )}

      {result?.failures.length ? (
        <Alert variant="error">
          {result.failures.map((failure) => `${failure.url}: ${failure.error}`).join('\n')}
        </Alert>
      ) : null}

      {trackedEntries.length > 0 && (
        <Card className="p-4 space-y-3 bg-surface-subtle">
          <div className="space-y-1">
            <h4 className="text-body-sm font-medium text-text-primary">
              {anyTrackedGenerating
                ? `Generating ${trackedEntries.length} refresh drafts`
                : `${readyTrackedItems.length} page${readyTrackedItems.length === 1 ? '' : 's'} ready for review`}
            </h4>
            <p className="text-body-xs text-text-secondary">
              Pages only appear in the Library once generation is complete. While the engine is working, progress stays here.
            </p>
          </div>

          {anyTrackedGenerating && (
            <div className="space-y-2">
              {trackedItems.map(({ entry, query, meta }) => (
                <div
                  key={entry.contentItemId}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-surface-primary px-4 py-3"
                >
                  <div className="space-y-1">
                    <div className="text-body-sm font-medium text-text-primary">{entry.title}</div>
                    <div className="text-body-xs text-text-tertiary">
                      {meta.error
                        ? String(meta.error)
                        : query?.isLoading || meta.generating
                          ? String(meta.phase ?? 'Generating proposed update...')
                          : 'Ready for review'}
                    </div>
                  </div>
                  {query?.isLoading || meta.generating ? (
                    <div className="flex items-center gap-2 text-body-xs text-text-secondary">
                      <Spinner className="w-4 h-4" />
                      Generating
                    </div>
                  ) : meta.error ? (
                    <Badge variant="error">Failed</Badge>
                  ) : (
                    <Badge variant="success">Ready</Badge>
                  )}
                </div>
              ))}
            </div>
          )}

          {readyTrackedItems.length > 0 && (
            <div className="rounded-xl border border-border bg-surface-primary px-4 py-4 space-y-3">
              <div className="space-y-1">
                <div className="text-body-sm font-medium text-text-primary">
                  {readyTrackedItems.length} draft{readyTrackedItems.length === 1 ? '' : 's'} ready for review
                </div>
                <div className="text-body-xs text-text-secondary">
                  Generation is complete. Review the drafts from the Library, where scores, history, and the full website editor are available.
                </div>
              </div>
              <Button size="sm" variant="secondary" onClick={onGoToLibrary}>
                Go to Library
              </Button>
            </div>
          )}

          {failedTrackedItems.length > 0 && (
            <div className="space-y-2">
              {failedTrackedItems.map(({ entry, meta }) => (
                <Alert key={entry.contentItemId} variant="error">
                  {entry.title}: {String(meta.error)}
                </Alert>
              ))}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

/* ─── Generation Progress ─── */

const PHASE_ORDER: { match: RegExp; label: string }[] = [
  { match: /^Starting/i, label: 'Queued' },
  { match: /^Researching/i, label: 'Researching topic' },
  { match: /Extracting entities/i, label: 'Analysing competitors' },
  { match: /link vocabulary/i, label: 'Building internal links' },
  { match: /content brief/i, label: 'Loading Notion brief' },
  { match: /^Generating/i, label: 'Drafting content' },
  { match: /^Scoring/i, label: 'Scoring quality' },
  { match: /Entity revision/i, label: 'Refining for SEO' },
  { match: /^Revising/i, label: 'Revising weak sections' },
  { match: /^Saving/i, label: 'Saving' },
  { match: /internal links/i, label: 'Recording links' },
];

function friendlyPhase(phase?: string): string {
  if (!phase) return 'Starting…';
  const match = PHASE_ORDER.find((p) => p.match.test(phase));
  return match ? match.label : phase.replace(/\.\.\.$/, '');
}

function phaseIndex(phase?: string): number {
  if (!phase) return 0;
  const idx = PHASE_ORDER.findIndex((p) => p.match.test(phase));
  return idx >= 0 ? idx : 0;
}

function GenerationProgress({ phase, startedAt }: { phase?: string; startedAt: string }) {
  const [elapsed, setElapsed] = useState(() => Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000));

  useEffect(() => {
    const id = setInterval(() => {
      setElapsed(Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [startedAt]);

  const idx = phaseIndex(phase);
  const total = PHASE_ORDER.length;
  const pct = Math.min(100, Math.round(((idx + 1) / total) * 100));

  const mins = Math.floor(elapsed / 60);
  const secs = elapsed % 60;
  const elapsedStr = mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-body-xs">
        <span className="text-text-secondary font-medium">{friendlyPhase(phase)}…</span>
        <span className="text-text-tertiary tabular-nums">{elapsedStr} elapsed · typically 1–3 min</span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-surface-secondary">
        <div
          className="h-full bg-text-primary transition-all duration-500 ease-out"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

/* ─── LinkedIn Result Display ─── */

interface LinkedInPost {
  hook: string;
  body: string;
  cta: string;
  full_text: string;
  angle: string;
}

interface LinkedInScore {
  hook_score: number;
  engagement_score: number;
  tone_score: number;
  value_score: number;
  brand_score: number;
  comment_bait_score?: number;
  conciseness_score?: number;
  overall_score: number;
}

interface LinkedInVariation {
  post: LinkedInPost;
  score: LinkedInScore;
  revised: boolean;
}

function LinkedInResult({ data }: { data: Record<string, unknown> }) {
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [selectedIdx, setSelectedIdx] = useState(0);

  const bestPost = data.bestPost as LinkedInVariation | undefined;
  const allVariations = (data.allVariations as LinkedInVariation[] | undefined) ?? [];

  if (!bestPost) return null;

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const renderPost = (text: string) =>
    text.split('\n\n').map((paragraph, i) => (
      <p key={i} className={i > 0 ? 'mt-4' : ''}>
        {paragraph.split('\n').map((line, j) => (
          <span key={j}>
            {j > 0 && <br />}
            {line}
          </span>
        ))}
      </p>
    ));

  const scoreColor = (value: number) =>
    value >= 8 ? 'text-success' : value >= 6 ? 'text-warning' : 'text-error';

  const scoreEntries = (score: LinkedInScore) => {
    const entries: { label: string; value: number }[] = [
      { label: 'Hook', value: score.hook_score },
      { label: 'Engage', value: score.engagement_score },
    ];
    if (score.comment_bait_score != null) entries.push({ label: 'Bait', value: score.comment_bait_score });
    if (score.conciseness_score != null) entries.push({ label: 'Concise', value: score.conciseness_score });
    entries.push(
      { label: 'Tone', value: score.tone_score },
      { label: 'Value', value: score.value_score },
      { label: 'Brand', value: score.brand_score },
    );
    return entries;
  };

  const selected = allVariations[selectedIdx] ?? bestPost;
  const charCount = selected.post.full_text.length;

  return (
    <div className="space-y-4">
      {/* Variation selector */}
      {allVariations.length > 1 && (
        <div className="flex gap-2 items-center">
          <span className="text-body-xs text-text-tertiary">Variation:</span>
          {allVariations.map((v, i) => (
            <button
              key={i}
              onClick={() => setSelectedIdx(i)}
              className={`px-3 py-1 text-body-xs rounded-md border transition-colors ${
                selectedIdx === i
                  ? 'border-text-primary bg-surface-secondary text-text-primary font-medium'
                  : 'border-border text-text-tertiary hover:text-text-secondary'
              }`}
            >
              {v === bestPost ? `#${i + 1} (Best)` : `#${i + 1}`}
            </button>
          ))}
        </div>
      )}

      {/* Post display */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Badge variant="success">
              Score: {selected.score.overall_score.toFixed(1)}/10
            </Badge>
            <Badge variant="default">{selected.post.angle}</Badge>
            {selected.revised && <Badge variant="warning">Revised</Badge>}
            <span className="text-body-xs text-text-tertiary">{charCount} chars</span>
          </div>
          <Button
            variant="primary"
            size="sm"
            onClick={() => copyToClipboard(selected.post.full_text, `post-${selectedIdx}`)}
          >
            {copiedId === `post-${selectedIdx}` ? 'Copied!' : 'Copy to Clipboard'}
          </Button>
        </div>

        {/* Ready-to-paste post */}
        <div className="border-l-4 border-brand pl-5 text-body-md text-text-primary leading-relaxed select-all">
          {renderPost(selected.post.full_text)}
        </div>

        {/* Score breakdown */}
        <div className={`grid gap-2 mt-4 pt-4 border-t border-border`} style={{ gridTemplateColumns: `repeat(${scoreEntries(selected.score).length}, 1fr)` }}>
          {scoreEntries(selected.score).map((s) => (
            <div key={s.label} className="text-center">
              <p className="text-body-xs text-text-tertiary">{s.label}</p>
              <p className={`text-body-md font-semibold ${scoreColor(s.value)}`}>{s.value}/10</p>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

/* ─── Extra Field Renderer ─── */

function ExtraField({ config, value, onChange }: {
  config: ExtraFieldConfig;
  value: unknown;
  onChange: (v: unknown) => void;
}) {

  if (config.type === 'select' && config.options) {
    return (
      <div>
        <label className="block text-body-xs font-medium text-text-secondary mb-1">
          {config.label}{config.required && <span className="text-error ml-0.5">*</span>}
        </label>
        <Select
          value={String(value ?? config.defaultValue ?? '')}
          onChange={(e) => onChange(e.target.value)}
          className="w-full"
        >
          {config.options.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </Select>
      </div>
    );
  }

  if (config.type === 'textarea') {
    return (
      <div className="md:col-span-2">
        <label className="block text-body-xs font-medium text-text-secondary mb-1">
          {config.label}{config.required && <span className="text-error ml-0.5">*</span>}
        </label>
        <Textarea
          value={String(value ?? '')}
          onChange={(e) => onChange(e.target.value)}
          placeholder={config.placeholder}
          rows={3}
        />
      </div>
    );
  }

  return (
    <div>
      <label className="block text-body-xs font-medium text-text-secondary mb-1">
        {config.label}{config.required && <span className="text-error ml-0.5">*</span>}
      </label>
      <Input
        type={config.type === 'number' ? 'number' : 'text'}
        value={String(value ?? '')}
        onChange={(e) => onChange(config.type === 'number' ? Number(e.target.value) || '' : e.target.value)}
        placeholder={config.placeholder}
      />
    </div>
  );
}

/* ─── Library Tab ─── */

interface LibraryTabProps {
  /** Which half of the library to show: website pages under Website, posts under LinkedIn. */
  channel: 'website' | 'linkedin';
  onOpenRewriteProject: (contentItemId: string) => void;
  /** Open straight on this group / status (a status tile above was clicked). */
  initialGroup?: LibraryGroup | null;
  initialStatus?: StatusFilter | '';
}

/** LinkedIn authors, in the order they appear in the overview. */
const LINKEDIN_PERSONS: Array<{ key: string; label: string }> = [
  { key: 'patrick', label: 'Patrick (CEO)' },
  { key: 'evrard', label: 'Evrard (CTO)' },
  { key: 'sequesto', label: 'SEQUESTO' },
];
const PERSON_LABEL: Record<string, string> = Object.fromEntries(
  LINKEDIN_PERSONS.map((p) => [p.key, p.label]),
);

/**
 * What the library drill-in shows: one author or page type, every item of the
 * channel (a status tile was clicked; the status filter is preset), or the
 * website pages live on the site.
 */
type LibraryGroup =
  | { channel: 'linkedin'; key: string }
  | { channel: 'website'; key: ContentType | 'all' | 'live' };

function groupHeading(group: LibraryGroup): string {
  if (group.key === 'all') return group.channel === 'linkedin' ? 'All LinkedIn posts' : 'All website pages';
  if (group.key === 'live') return 'Live on sequesto.com';
  return group.channel === 'linkedin'
    ? `${PERSON_LABEL[group.key] ?? group.key}: LinkedIn posts`
    : `${TYPE_LABELS[group.key as ContentType] ?? group.key} pages`;
}

const NO_IDS: string[] = [];

/** Recent LinkedIn posts: fetch this many, show this many. */
const RECENT_FETCH = 24;
const RECENT_SHOWN = 6;

/** A draft untouched this long, with no future date, is treated as stale (an event or moment that passed). */
const STALE_DRAFT_DAYS = 14;

/**
 * Recent posts, newest first by last update (the date each card shows).
 * - A draft whose planned date has passed, or that has sat untouched for
 *   {@link STALE_DRAFT_DAYS} days with no date, was for a moment that's gone
 *   ("Tomorrow we're at Transport & Logistics 2026"): hidden here and counted.
 * - Variants of one post share a title: one card per title, with the count.
 * Nothing is deleted; every draft stays in the author drill-in.
 */
function recentPostsView(
  items: ContentItem[],
  now = new Date(),
): { shown: Array<{ item: ContentItem; variants: number }>; hiddenPastDrafts: number } {
  const today = now.toISOString().slice(0, 10);
  const staleBefore = new Date(now.getTime() - STALE_DRAFT_DAYS * 86_400_000).toISOString();
  const isStaleDraft = (i: ContentItem) =>
    i.status === 'draft' && (i.publishAt ? i.publishAt.slice(0, 10) < today : i.updatedAt < staleBefore);
  const kept = items.filter((i) => !isStaleDraft(i));
  const byTitle = new Map<string, { item: ContentItem; variants: number }>();
  for (const i of [...kept].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))) {
    const key = (i.title ?? '').trim().toLowerCase();
    const hit = byTitle.get(key);
    if (hit) hit.variants++;
    else byTitle.set(key, { item: i, variants: 1 });
  }
  return { shown: [...byTitle.values()].slice(0, RECENT_SHOWN), hiddenPastDrafts: items.length - kept.length };
}

type StatusFilter = NonNullable<ContentItemFilters['status']>;

/**
 * The answer-first row over the library: how much is draft, in review,
 * approved and live. Each tile opens the matching items. Website "Live" is read
 * from Sanity (a published document exists), not from the OS status: pages
 * published in the Studio never move to "published" in the OS, which is why
 * this tile read 0 of 103 until 9 Oct.
 */
function LibraryStatusRow({
  summary,
  channel,
  onStatus,
  onLive,
}: {
  summary: LibrarySummary;
  channel: 'website' | 'linkedin';
  onStatus: (status: StatusFilter) => void;
  onLive: () => void;
}) {
  const counts = summary[channel].byStatus;
  // Approved posts nobody posted: an exception, not "ready to post" (9 on 12 Oct, all 89+ days old).
  const approvedPosts = useContentItems(
    { type: 'linkedin_post', status: 'approved', archived: 'active', limit: 200 },
    { enabled: channel === 'linkedin' && (counts?.approved ?? 0) > 0 },
  );
  if (!counts) {
    return <p className="text-body-xs text-text-tertiary">Status counts unavailable from the API.</p>;
  }
  const staleDays = (approvedPosts.data?.items ?? [])
    .map((i) => staleApprovalDays(i))
    .filter((d): d is number => d !== null);
  const total = summary[channel].total;
  const live = channel === 'website' ? summary.website.live : undefined;
  const of = (n: number) => (total > 0 ? ` · ${Math.round((n / total) * 100)}% of ${total}` : '');
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatTile label="Draft" value={counts.draft} sub={`Not yet sent for review${of(counts.draft)}`} onClick={() => onStatus('draft')} disabled={counts.draft === 0} />
        <StatTile label="In review" value={counts.review} sub={`Waiting on a reviewer${of(counts.review)}`} onClick={() => onStatus('review')} disabled={counts.review === 0} />
        <StatTile
          label="Approved"
          value={counts.approved}
          sub={`Ready to publish${of(counts.approved)}`}
          onClick={() => onStatus('approved')}
          disabled={counts.approved === 0}
        />
        {channel === 'linkedin' ? (
          <StatTile label="Published" value={counts.published} sub={`Marked as published${of(counts.published)}`} onClick={() => onStatus('published')} disabled={counts.published === 0} />
        ) : !live || live.count === null ? (
          <StatTile
            label="Live on the site"
            value="—"
            sub={live?.error ? 'Sanity unavailable' : 'Unavailable from the API'}
            tooltip={live?.error ?? undefined}
          />
        ) : (
          <StatTile
            label="Live on the site"
            value={live.count}
            sub="Published in Sanity · overlaps the three tiles to the left"
            tooltip="A page is live when Sanity holds a published version of it, whoever published it. It can also be in draft or review in the OS when a new version is being worked on."
            onClick={onLive}
            disabled={live.count === 0}
          />
        )}
      </div>
      {channel === 'linkedin' && approvedPosts.isError && (
        <ErrorState compact title="Couldn’t check approved posts for ones left unpublished" error={approvedPosts.error} onRetry={() => void approvedPosts.refetch()} />
      )}
      {staleDays.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-body-xs text-text-secondary">
          <Badge variant="warning">Approved, not published</Badge>
          <span>
            {staleDays.length} of {counts.approved} approved {counts.approved === 1 ? 'post has' : 'posts have'} waited more
            than {STALE_APPROVAL_DAYS} days without being marked as published (the oldest {Math.max(...staleDays)} days).
            Publish them, or move them back to draft.
          </span>
          <button type="button" className="text-accent hover:underline" onClick={() => onStatus('approved')}>
            Open them
          </button>
        </div>
      )}
      <p className="text-body-xs text-text-tertiary">
        All time.{' '}
        {channel === 'website' &&
          `Draft, in review and approved are the OS workflow: each page once, adding up to ${total}. Live is read from Sanity, so a live page with a new version in progress counts there and in one of the three.`}
      </p>
    </div>
  );
}

function LibraryTab({ channel, onOpenRewriteProject, initialGroup = null, initialStatus = '' }: LibraryTabProps) {
  const navigate = useNavigate();
  const PAGE_SIZE = 24;
  const [group, setGroup] = useState<LibraryGroup | null>(initialGroup);
  const [status, setStatus] = useState<ContentItemFilters['status'] | ''>(initialStatus);
  const [expandedTitles, setExpandedTitles] = useState<Set<string>>(() => new Set());
  const [language, setLanguage] = useState<ContentLanguage | ''>('');
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [regenTarget, setRegenTarget] = useState<ContentItem | null>(null);
  const [voiceSamplesOpen, setVoiceSamplesOpen] = useState(false);
  const [impactDialogOpen, setImpactDialogOpen] = useState(false);

  const summary = useContentLibrarySummary();

  // Build the drill-in filters from the selected group. Website groups opt into
  // hidden + any-archive so live/pushed pages appear; LinkedIn stays 'active' so
  // used-up sibling variations stay hidden.
  const base: ContentItemFilters = {
    limit: PAGE_SIZE,
    offset,
    status: status || undefined,
    language: language || undefined,
    search: search || undefined,
  };
  const liveIds = summary.data?.website.live?.ids ?? NO_IDS;
  const filters: ContentItemFilters = !group
    ? { limit: PAGE_SIZE }
    : group.channel === 'linkedin'
      ? group.key === 'all'
        ? { ...base, type: 'linkedin_post', archived: 'active' }
        : { ...base, type: 'linkedin_post', person: group.key as ContentItemFilters['person'], archived: 'active' }
      : group.key === 'all'
        ? { ...base, channel: 'website', archived: 'any', includeHidden: true }
        : group.key === 'live'
          ? { ...base, ids: liveIds, archived: 'any', includeHidden: true }
          : { ...base, type: group.key, archived: 'any', includeHidden: true };

  const { data, isLoading } = useContentItems(filters, { enabled: !!group });
  const pageGroups = useMemo(() => groupByTitle(data?.items ?? []), [data]);
  const pageGroupCount = pageGroups.length;
  // The last few posts, so the LinkedIn view shows output without a drill-in. A wider
  // fetch than shown, so hiding past-date drafts still leaves a full row.
  const recentPosts = useContentItems(
    { type: 'linkedin_post', archived: 'active', limit: RECENT_FETCH },
    { enabled: channel === 'linkedin' && !group },
  );
  const recent = useMemo(() => recentPostsView(recentPosts.data?.items ?? []), [recentPosts.data]);
  const selectedItem = useContentItem(selectedId);
  const deleteItem = useDeleteContentItem();
  const reopenItem = useReopenWebsiteReview();
  const generateRefresh = useGenerateWebsiteRefresh();

  function selectGroup(next: LibraryGroup, presetStatus: ContentItemFilters['status'] | '' = '') {
    setGroup(next);
    setOffset(0);
    setStatus(presetStatus);
    setLanguage('');
    setSearch('');
  }

  async function handleDelete(item: ContentItem) {
    if (!confirm(`Delete "${item.title}" from the library?`)) return;
    await deleteItem.mutateAsync(item.id);
    if (selectedId === item.id) {
      setSelectedId(null);
    }
  }

  async function handleReopen(item: ContentItem) {
    const ok = window.confirm(
      `Re-open "${item.title}" for manual edits?\n\nThis pulls the current Sanity content into a fresh draft and clears any previous review queue. The previous version is preserved in the version history.`,
    );
    if (!ok) return;
    await reopenItem.mutateAsync(item.id);
  }

  return (
    <div id="content-library" className="scroll-mt-6 space-y-4 border-t border-border-light pt-6">
      {/* The status tiles sit at the top of the page (ContentDashboard), above the generator. */}
      {/* Header: the library's counts in one line, plus this channel's import action. */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-baseline gap-3">
          <h2 className="text-body-md font-semibold text-text-primary">Library</h2>
          {summary.data && (
            <span className="text-body-sm text-text-tertiary tabular-nums">
              {channel === 'website'
                ? `${summary.data.website.total} pages`
                : `${summary.data.linkedin.total} posts · ${summary.data.linkedin.byPerson.reduce((n, r) => n + (r.posted ?? 0), 0)} published`}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {channel === 'website' ? (
            <Button size="sm" variant="secondary" onClick={() => setImpactDialogOpen(true)}>
              New impact article
            </Button>
          ) : (
            <Button size="sm" variant="secondary" onClick={() => setVoiceSamplesOpen(true)}>
              Import published posts
            </Button>
          )}
        </div>
      </div>

      <ImpactArticleDialog isOpen={impactDialogOpen} onClose={() => setImpactDialogOpen(false)} />

      {!group ? (
        /* ─── Overview: one row per author or page type ─── */
        summary.error ? (
          <ErrorState title="Couldn’t load the library" error={summary.error} onRetry={() => summary.refetch()} />
        ) : summary.isLoading ? (
          <LoadingState />
        ) : channel === 'linkedin' ? (
          <>
          <Card className="p-4">
            <div className="divide-y divide-border-light">
              {LINKEDIN_PERSONS.map((p) => {
                const row = summary.data?.linkedin.byPerson.find((r) => r.person === p.key);
                const total = row?.total ?? 0;
                const posted = row?.posted ?? 0;
                return (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => selectGroup({ channel: 'linkedin', key: p.key })}
                    title={`Open ${p.label}’s ${total} ${total === 1 ? 'post' : 'posts'}`}
                    className="group flex w-full items-center justify-between rounded-sm py-2 text-left hover:bg-surface-secondary"
                  >
                    <span className="text-body-sm text-text-secondary group-hover:text-text-primary">{p.label}</span>
                    <span className="flex items-center gap-2 text-body-sm text-text-primary tabular-nums">
                      <span>{total}</span>
                      {posted > 0 && <span className="text-body-xs text-text-tertiary">· {posted} published</span>}
                      <ChevronRight className="h-4 w-4 text-text-tertiary group-hover:text-accent" aria-hidden />
                    </span>
                  </button>
                );
              })}
            </div>
          </Card>
          {recentPosts.error ? (
            <ErrorState compact title="Couldn’t load recent posts" error={recentPosts.error} onRetry={() => recentPosts.refetch()} />
          ) : recent.shown.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-body-sm font-medium text-text-secondary">
                Recent posts
                <span className="ml-2 font-normal text-body-xs text-text-tertiary">Newest first, by last update</span>
              </h3>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {recent.shown.map(({ item, variants }) => (
                  // Status stays on every card here: a mix of drafts and posted items is the point of the row.
                  <ContentItemCard key={item.id} item={item} hideType olderVersions={variants - 1} onSelect={() => setSelectedId(item.id)} />
                ))}
              </div>
              <p className="text-body-xs text-text-tertiary">
                {[
                  recent.hiddenPastDrafts > 0 &&
                    `${recent.hiddenPastDrafts} stale ${recent.hiddenPastDrafts === 1 ? 'draft' : 'drafts'} hidden (planned date passed, or untouched ${STALE_DRAFT_DAYS}+ days with no date); still in each author's list`,
                  'No engagement shown: no LinkedIn post metrics have been synced yet',
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
            </div>
          )}
          </>
        ) : (
          <Card className="p-4">
            <div className="divide-y divide-border-light">
              {(summary.data?.website.byType ?? [])
                .filter((r) => r.total > 0)
                .map((r) => (
                  <button
                    key={r.type}
                    type="button"
                    onClick={() => selectGroup({ channel: 'website', key: r.type })}
                    title={`Open the ${r.total} ${TYPE_LABELS[r.type] ?? r.type} ${r.total === 1 ? 'page' : 'pages'}`}
                    className="group flex w-full items-center justify-between rounded-sm py-2 text-left hover:bg-surface-secondary"
                  >
                    <span className="text-body-sm text-text-secondary group-hover:text-text-primary">{TYPE_LABELS[r.type] ?? r.type}</span>
                    <span className="flex items-center gap-1 text-body-sm text-text-primary tabular-nums">
                      {r.total}
                      <ChevronRight className="h-4 w-4 text-text-tertiary group-hover:text-accent" aria-hidden />
                    </span>
                  </button>
                ))}
            </div>
            {(summary.data?.website.byType ?? []).filter((r) => r.total > 0).length === 0 && (
              <EmptyState compact title="No website pages yet" />
            )}
          </Card>
        )
      ) : (
        /* ─── Drill-in: cards for the selected group ─── */
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <Button size="sm" variant="ghost" onClick={() => setGroup(null)}>← All {channel === 'website' ? 'pages' : 'posts'}</Button>
            <h3 className="text-body-sm font-semibold text-text-primary">{groupHeading(group)}</h3>
          </div>

          <div className="flex flex-wrap gap-3">
            <Select
              size="sm"
              aria-label="Status"
              value={status}
              onChange={(e) => { setStatus((e.target.value || '') as ContentItemFilters['status'] | ''); setOffset(0); }}
            >
              <option value="">All statuses</option>
              <option value="draft">Draft</option>
              <option value="review">In review</option>
              <option value="approved">Approved</option>
              <option value="published">Published</option>
            </Select>
            <Select
              size="sm"
              aria-label="Language"
              value={language}
              onChange={(e) => { setLanguage((e.target.value || '') as ContentLanguage | ''); setOffset(0); }}
            >
              <option value="">All languages</option>
              {CONTENT_LANGUAGES.map((l) => (
                <option key={l} value={l}>{l.toUpperCase()}</option>
              ))}
            </Select>
            <Input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setOffset(0); }}
              placeholder="Search..."
              className="max-w-xs"
            />
          </div>

          {isLoading ? (
            <LoadingState />
          ) : data?.items.length === 0 ? (
            <Card><EmptyState compact title="No content items found" /></Card>
          ) : (
            <>
              <SharedPills items={data?.items ?? []} block />
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {pageGroups.flatMap(({ item: head, older }) =>
                  expandedTitles.has(head.id) ? [{ item: head, older: [] as ContentItem[] }, ...older.map((o) => ({ item: o, older: [] as ContentItem[] }))] : [{ item: head, older }],
                ).map(({ item, older }, _i, cards) => {
                  const items = cards.map((c) => c.item);
                  const oneType = isConstant(items, (x) => x.type);
                  const oneStatus = isConstant(items, getDisplayStatusLabel);
                  const isRewrite = (item.metadata as { rewrite?: boolean } | undefined)?.rewrite === true;
                  const isWebsiteRefresh = !!((item.metadata as Record<string, unknown> | undefined)?.websiteRefresh);
                  return (
                    <ContentItemCard
                      key={item.id}
                      item={item}
                      hideType={oneType}
                      hideStatus={oneStatus}
                      olderVersions={older.length}
                      onShowVersions={() => setExpandedTitles((prev) => new Set(prev).add(item.id))}
                      onDelete={() => {
                        void handleDelete(item);
                      }}
                      isDeleting={deleteItem.isPending && deleteItem.variables === item.id}
                      onReopen={isWebsiteRefresh && !item.archivedAt ? () => {
                        void handleReopen(item);
                      } : undefined}
                      isReopening={reopenItem.isPending && reopenItem.variables === item.id}
                      onRegenerate={isWebsiteRefresh && item.archivedAt ? () => setRegenTarget(item) : undefined}
                      isRegenerating={generateRefresh.isPending && generateRefresh.variables?.id === item.id}
                      onSelect={() => {
                        if (isRewrite) {
                          onOpenRewriteProject(item.id);
                        } else if (isWebsiteRefresh) {
                          navigate(`/marketing/content/library/${item.id}`, { state: { fromLibrary: true } });
                        } else {
                          setSelectedId(item.id);
                        }
                      }}
                    />
                  );
                })}
              </div>
              {data && data.total > PAGE_SIZE && (
                <div className="flex justify-center gap-2 pt-4">
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={offset === 0}
                    onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                  >
                    Previous
                  </Button>
                  <span className="text-body-xs text-text-tertiary self-center">
                    {/* The API pages items; cards fold versions of one title. One count (items), then what folding did. */}
                    Page {Math.floor(offset / PAGE_SIZE) + 1} of {Math.ceil(data.total / PAGE_SIZE)} · items {offset + 1}–
                    {Math.min(offset + PAGE_SIZE, data.total)} of {data.total}
                    {data.items.length > pageGroupCount &&
                      ` · ${data.items.length - pageGroupCount} older ${data.items.length - pageGroupCount === 1 ? 'version' : 'versions'} folded under ${data.items.length - pageGroupCount === 1 ? 'its' : 'their'} newest card`}
                  </span>
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={offset + PAGE_SIZE >= data.total}
                    onClick={() => setOffset(offset + PAGE_SIZE)}
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Detail Modal */}
      {selectedId && selectedItem.data && (
        <ContentDetailModal
          item={selectedItem.data}
          onClose={() => setSelectedId(null)}
          onDeleted={() => setSelectedId(null)}
        />
      )}

      {/* Re-generate dialog (archived → active) */}
      {regenTarget && (
        <RegenerateDialog
          isOpen
          itemTitle={regenTarget.title}
          isPending={generateRefresh.isPending}
          onClose={() => setRegenTarget(null)}
          onConfirm={async (input) => {
            const target = regenTarget;
            setRegenTarget(null);
            try {
              await generateRefresh.mutateAsync({ id: target.id, input });
            } catch (error) {
              const message = error instanceof Error ? error.message : 'Failed to start re-generation';
              window.alert(message);
            }
          }}
        />
      )}

      <VoiceSamplesModal
        isOpen={voiceSamplesOpen}
        onClose={() => setVoiceSamplesOpen(false)}
        initialPerson={(group?.channel === 'linkedin' ? (group.key as VoicePersona) : undefined) ?? 'evrard'}
      />
    </div>
  );
}

/* ─── Content Detail Modal with Review Workflow ─── */

function ContentDetailModal({
  item,
  onClose,
  onDeleted,
}: {
  item: ContentItem;
  onClose: () => void;
  onDeleted?: () => void;
}) {
  const { user: currentUser } = useAuth();
  const deleteItem = useDeleteContentItem();
  const patchItem = usePatchContentItem();
  const approveItem = useApproveContentItem();
  const sanityExport = useExportToSanity();
  const sanityTypesQuery = useSanityTypes();
  const [publishNote, setPublishNote] = useState<string | null>(null);
  const [sanityResult, setSanityResult] = useState<{ message: string; error?: boolean } | null>(null);
  const [showSanityTypePicker, setShowSanityTypePicker] = useState(false);
  const [selectedSanityType, setSelectedSanityType] = useState<string>('');
  const [schemaProposal, setSchemaProposal] = useState<
    | { documentTypeName: string; suggestedFilename: string; tsCode: string; notes: string }
    | null
  >(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedTitle, setEditedTitle] = useState(item.title);
  const [editedBody, setEditedBody] = useState(item.body ?? '');
  const [showVersions, setShowVersions] = useState(false);
  const [versionPreview, setVersionPreview] = useState<ContentVersion | null>(null);
  const versionsQuery = useContentVersions(showVersions ? item.id : null);
  const restoreVersion = useRestoreContentVersion();

  const meta = item.metadata as Record<string, unknown>;
  const reviewMeta = meta?.review as ReviewMeta | undefined;
  const websiteRefresh = meta?.websiteRefresh as Record<string, unknown> | undefined;
  const sourceUrl = typeof websiteRefresh?.sourceUrl === 'string' ? websiteRefresh.sourceUrl : item.externalRef;
  const routeFamily = typeof websiteRefresh?.routeFamily === 'string' ? websiteRefresh.routeFamily : null;
  const snapshotKind = typeof websiteRefresh?.snapshotKind === 'string' ? websiteRefresh.snapshotKind : null;

  const isCurrentUserReviewer = reviewMeta?.reviewers.find((r) => r.email === currentUser?.email || r.userId === currentUser?.id);
  const canApprove = item.status === 'review' && isCurrentUserReviewer?.status === 'pending';

  const approvedCount = reviewMeta?.reviewers.filter((r) => r.status === 'approved').length ?? 0;
  const totalReviewers = reviewMeta?.reviewers.length ?? 0;

  async function handleApprove() {
    await approveItem.mutateAsync(item.id);
  }

  async function handleStatusChange(status: 'draft' | 'review' | 'approved' | 'published') {
    await patchItem.mutateAsync({ id: item.id, updates: { status } });
  }

  const isLinkedIn = item.type === 'linkedin_post';
  const similarity = (item.metadata?.similarity ?? null) as { score?: number; mostSimilarTitle?: string | null } | null;
  const isTooSimilar = typeof similarity?.score === 'number' && similarity.score >= 0.85;

  // LinkedIn posts have no publish API — the author copies the text into LinkedIn
  // and marks it published here, which archives the unchosen sibling variations.
  async function handleMarkPublished() {
    const result = (await patchItem.mutateAsync({
      id: item.id,
      updates: { status: 'published' },
    })) as { archivedSiblingCount?: number };
    const archived = result.archivedSiblingCount ?? 0;
    setPublishNote(
      archived > 0
        ? `Marked published. ${archived} other variation${archived === 1 ? '' : 's'} from this batch archived.`
        : 'Marked published.',
    );
  }

  async function handleDeleteItem() {
    if (!confirm(`Delete "${item.title}" from the library?`)) return;
    await deleteItem.mutateAsync(item.id);
    onDeleted?.();
    onClose();
  }

  async function handleRestoreVersion(version: ContentVersion) {
    const confirmed = window.confirm(
      `Restore saved version v${version.versionNum}? The current state will be snapshotted first so you can undo this restore if needed.`,
    );
    if (!confirmed) return;
    try {
      await restoreVersion.mutateAsync({ contentItemId: item.id, versionId: version.id });
      setVersionPreview(null);
      setIsEditing(false);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to restore saved version';
      window.alert(message);
    }
  }

  // Split title into heading (first sentence) and subtitle (rest)
  const dotIdx = item.title.indexOf('. ');
  const titleHeading = dotIdx > 0 ? item.title.slice(0, dotIdx + 1) : item.title;
  const titleContext = dotIdx > 0 ? item.title.slice(dotIdx + 2) : undefined;

  return (
    <Modal isOpen onClose={onClose} title={titleHeading} description={titleContext}>
      <div className="space-y-4">
        <div className="flex flex-wrap gap-2">
          <Badge variant="default">{TYPE_LABELS[item.type] ?? item.type}</Badge>
          <Badge variant={getDisplayStatusVariant(item)}>
            {getDisplayStatusLabel(item)}
          </Badge>
          <Badge variant="default">{item.language.toUpperCase()}</Badge>
          {isImportedPost(item) && <Badge variant="default">Imported</Badge>}
          {isTooSimilar && (
            <Badge variant="warning">
              Similar to a published post{similarity?.mostSimilarTitle ? `: "${similarity.mostSimilarTitle}"` : ''}
            </Badge>
          )}
        </div>

        <div className="space-y-2">
          {websiteRefresh && (
            <div className="border border-border rounded-md p-3 space-y-2 bg-surface-subtle">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="default">Imported website page</Badge>
                  {routeFamily && <Badge variant="default">{routeFamily}</Badge>}
                  {snapshotKind && <Badge variant="default">{snapshotKind}</Badge>}
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setShowVersions((v) => !v)}
                >
                  {showVersions ? 'Hide history' : 'History'}
                </Button>
              </div>
              {sourceUrl && (
                <a
                  href={sourceUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-body-xs text-text-secondary underline underline-offset-2"
                >
                  Open live page
                </a>
              )}
            </div>
          )}

          {websiteRefresh ? (
            <WebsiteReviewPanel contentItemId={item.id} />
          ) : (
            <>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <p className="text-body-xs font-medium text-text-primary">Body</p>
                  {patchItem.isPending && <Spinner />}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setShowVersions((v) => !v)}
                  >
                    {showVersions ? 'Hide history' : 'History'}
                  </Button>
                  {!isEditing ? (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => {
                        setEditedTitle(item.title);
                        setEditedBody(item.body ?? '');
                        setIsEditing(true);
                      }}
                    >
                      Edit
                    </Button>
                  ) : (
                    <>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setIsEditing(false);
                          setEditedTitle(item.title);
                          setEditedBody(item.body ?? '');
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        variant="primary"
                        disabled={
                          patchItem.isPending ||
                          (editedTitle === item.title && editedBody === (item.body ?? ''))
                        }
                        onClick={async () => {
                          const updates: Record<string, unknown> = { editSource: 'manual_edit' };
                          if (editedTitle !== item.title) updates.title = editedTitle;
                          if (editedBody !== (item.body ?? '')) updates.body = editedBody;
                          await patchItem.mutateAsync({ id: item.id, updates: updates as never });
                          setIsEditing(false);
                        }}
                      >
                        Save & learn
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {isEditing ? (
                <div className="space-y-2">
                  <Input
                    value={editedTitle}
                    onChange={(e) => setEditedTitle(e.target.value)}
                    placeholder="Title"
                  />
                  <Textarea
                    value={editedBody}
                    onChange={(e) => setEditedBody(e.target.value)}
                    rows={20}
                    className="font-mono text-body-xs"
                    placeholder="Body (markdown / plain text)"
                  />
                  <p className="text-body-xs text-text-secondary">
                    Saving captures a version snapshot and queues a feedback rule extraction. Pending
                    rules show up in the Generation Rules tab for approval before they affect future
                    generations.
                  </p>
                </div>
              ) : (
                item.body && (
                  <div className="text-body-xs text-text-secondary whitespace-pre-wrap max-h-[40vh] overflow-y-auto border border-border rounded-md p-3 bg-surface-subtle">
                    {item.body}
                  </div>
                )
              )}
            </>
          )}

          {showVersions && (
            <div className="border border-border rounded-md p-3 space-y-2">
              <p className="text-body-xs font-medium text-text-primary">
                {websiteRefresh ? 'Refresh history' : 'Edit history'}
              </p>
              {versionsQuery.isLoading && <Spinner />}
              {versionsQuery.data?.versions.length === 0 && (
                <p className="text-body-xs text-text-secondary">No prior versions.</p>
              )}
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {versionsQuery.data?.versions.map((v) => (
                  <button
                    key={v.id}
                    type="button"
                    className="w-full text-left text-body-xs text-text-secondary hover:bg-surface-subtle rounded px-2 py-1 flex items-center justify-between"
                    onClick={() =>
                      setVersionPreview(v)
                    }
                  >
                    <span>
                      v{v.versionNum} · {formatDayMonthYearTime(v.createdAt)} ·{' '}
                      <span className="font-mono">{v.editSource}</span>
                    </span>
                    {v.diffSummary && <span className="italic truncate ml-2">{v.diffSummary}</span>}
                  </button>
                ))}
              </div>
              {versionPreview && (
                <div className="border-t border-border pt-2 space-y-1">
                  <div className="flex items-start justify-between gap-3">
                    <div className="space-y-1 min-w-0">
                      <p className="text-body-xs font-medium text-text-primary">
                        v{versionPreview.versionNum} preview: {versionPreview.title}
                      </p>
                      <p className="text-body-xs text-text-tertiary">
                        {formatDayMonthYearTime(versionPreview.createdAt)} · <span className="font-mono">{versionPreview.editSource}</span>
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="secondary"
                      isLoading={restoreVersion.isPending && restoreVersion.variables?.versionId === versionPreview.id}
                      onClick={() => {
                        void handleRestoreVersion(versionPreview);
                      }}
                    >
                      Restore
                    </Button>
                  </div>
                  {versionPreview.diffSummary && (
                    <p className="text-body-xs text-text-secondary">{versionPreview.diffSummary}</p>
                  )}
                  <div className="text-body-xs text-text-secondary whitespace-pre-wrap max-h-[30vh] overflow-y-auto">
                    {versionPreview.body || '(empty)'}
                  </div>
                </div>
              )}

          <div className="flex justify-end">
            <Button
              variant="danger"
              isLoading={deleteItem.isPending}
              onClick={() => {
                void handleDeleteItem();
              }}
            >
              Delete item
            </Button>
          </div>
            </div>
          )}
        </div>

        {/* Review Status Panel */}
        {item.status === 'review' && reviewMeta && (
          <div className="border border-border rounded-md p-3 space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-body-xs font-medium text-text-primary">Review Progress</p>
              <Badge variant={approvedCount === totalReviewers ? 'success' : 'warning'}>
                {approvedCount}/{totalReviewers} approved
              </Badge>
            </div>
            <div className="space-y-1">
              {reviewMeta.reviewers.map((r) => (
                <div key={r.userId} className="flex items-center justify-between text-body-xs">
                  <span className="text-text-secondary">{r.name}</span>
                  <Badge variant={r.status === 'approved' ? 'success' : 'default'}>
                    {r.status === 'approved' ? `Approved ${r.approvedAt ? formatDayMonthYear(r.approvedAt) : ''}` : 'Pending'}
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Admin Status Override */}
        <div className="flex items-center gap-2 pt-2 border-t border-border">
          <label className="text-body-xs text-text-secondary">Status:</label>
          <Select
            value={item.status}
            onChange={(e) => handleStatusChange(e.target.value as 'draft' | 'review' | 'approved' | 'published')}
            disabled={patchItem.isPending}
            size="sm"
          >
            <option value="draft">Draft</option>
            <option value="review">In review</option>
            <option value="approved">Approved</option>
            {(!websiteRefresh || item.status === 'published') && (
              <option value="published">Published</option>
            )}
          </Select>
          {websiteRefresh && item.status !== 'published' && (
            <span className="text-body-xs text-text-tertiary">
              Use Push to Sanity draft in the website review panel to send the reviewed version to Sanity.
            </span>
          )}
        </div>

        {publishNote && (
          <p className="text-body-xs text-success-dark pt-2">{publishNote}</p>
        )}

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2 pt-2 border-t border-border">
          {item.status === 'draft' && (
            <ReviewerPicker itemId={item.id} />
          )}

          {/* LinkedIn posts are posted manually, then marked published here to
              close the loop — reachable straight from draft (they rarely go
              through the website review flow). */}
          {isLinkedIn && item.status !== 'published' && (
            <Button size="sm" onClick={handleMarkPublished} disabled={patchItem.isPending}>
              {patchItem.isPending ? 'Marking…' : 'Mark as published'}
            </Button>
          )}

          {canApprove && (
            <Button size="sm" onClick={handleApprove} disabled={approveItem.isPending}>
              {approveItem.isPending ? 'Approving...' : 'Approve'}
            </Button>
          )}

          {item.status === 'review' && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleStatusChange('draft')}
              disabled={patchItem.isPending}
            >
              Back to Draft
            </Button>
          )}

          {item.status === 'approved' && (
            <>
              {!websiteRefresh && !isLinkedIn && (
                <Button
                  size="sm"
                  onClick={() => handleStatusChange('published')}
                  disabled={patchItem.isPending}
                >
                  Mark Published
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleStatusChange('review')}
                disabled={patchItem.isPending}
              >
                Back to Review
              </Button>
            </>
          )}

          {!websiteRefresh && (item.status === 'approved' || item.status === 'published') && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setSanityResult(null);
                setShowSanityTypePicker((prev) => !prev);
                if (!sanityTypesQuery.data) {
                  sanityTypesQuery.refetch();
                }
              }}
              disabled={sanityExport.isPending}
            >
              {sanityExport.isPending ? 'Exporting...' : (meta?.sanityDocumentId ? 'Re-export to Sanity' : 'Export to Sanity')}
            </Button>
          )}
        </div>

        {showSanityTypePicker && (
          <div className="border border-border-light rounded p-3 space-y-2 bg-surface-secondary">
            <p className="text-body-xs text-text-secondary font-medium">Select Sanity document type:</p>
            {sanityTypesQuery.isLoading && (
              <div className="flex items-center gap-2 text-body-xs text-text-tertiary">
                <Spinner size="sm" /> Loading Sanity types...
              </div>
            )}
            {sanityTypesQuery.isError && (
              <p className="text-body-xs text-error">
                {sanityTypesQuery.error instanceof Error
                  ? sanityTypesQuery.error.message
                  : 'Failed to load Sanity types. Is the integration configured?'}
              </p>
            )}
            {sanityTypesQuery.data && (
              <>
                <Select
                  className="w-full"
                  value={selectedSanityType}
                  onChange={(e) => setSelectedSanityType(e.target.value)}
                >
                  <option value="">Choose a type...</option>
                  {sanityTypesQuery.data.types.map((t) => (
                    <option key={t.name} value={t.name}>
                      {t.title || t.name} ({t.fieldCount} fields)
                    </option>
                  ))}
                </Select>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="primary"
                    disabled={!selectedSanityType || sanityExport.isPending}
                    onClick={async () => {
                      setSanityResult(null);
                      setSchemaProposal(null);
                      try {
                        const result = await sanityExport.mutateAsync({
                          id: item.id,
                          documentType: selectedSanityType,
                        }) as Record<string, unknown>;
                        if (result.status === 'schema_missing' && result.proposal) {
                          setSchemaProposal(result.proposal as never);
                          setSanityResult({
                            message:
                              'No matching schema found in Sanity. Paste the proposed TypeScript below into your Studio repo, deploy, then retry.',
                            error: true,
                          });
                          setShowSanityTypePicker(false);
                          return;
                        }
                        setSanityResult({ message: (result.message as string) ?? 'Exported.' });
                        setShowSanityTypePicker(false);
                        setSelectedSanityType('');
                      } catch (err) {
                        const msg = err instanceof Error ? err.message : 'Export failed';
                        setSanityResult({ message: msg, error: true });
                      }
                    }}
                  >
                    {sanityExport.isPending ? 'Mapping & pushing...' : 'Push'}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setShowSanityTypePicker(false);
                      setSelectedSanityType('');
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </>
            )}
          </div>
        )}

        {sanityResult && (
          <div className={`border rounded-md p-3 text-body-xs ${sanityResult.error ? 'border-error/30 bg-error-light text-error-dark' : 'border-success/30 bg-success-light text-success-dark'}`}>
            {sanityResult.message}
          </div>
        )}

        {schemaProposal && (
          <div className="border border-warning/30 bg-warning-light rounded p-3 space-y-2">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-body-xs font-medium text-warning-dark">
                  Proposed Sanity schema: <code>{schemaProposal.documentTypeName}</code>
                </p>
                <p className="text-body-xs text-warning-dark">
                  Save as <code>{schemaProposal.suggestedFilename}</code> in your Sanity Studio repo,
                  register it in the schema index, then run{' '}
                  <code>npx sanity schema deploy</code> and retry the export.
                </p>
                {schemaProposal.notes && (
                  <p className="text-body-xs text-warning-dark italic mt-1">
                    Notes: {schemaProposal.notes}
                  </p>
                )}
              </div>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  void navigator.clipboard.writeText(schemaProposal.tsCode);
                }}
              >
                Copy
              </Button>
            </div>
            <pre className="text-body-xs bg-surface-primary border border-warning/30 rounded p-2 max-h-[40vh] overflow-auto whitespace-pre">
              {schemaProposal.tsCode}
            </pre>
          </div>
        )}

        {/* Notion sources used during generation */}
        {(() => {
          const sources = meta?.notionSources as Array<{
            database: string;
            databaseId: string;
            pageId?: string;
            pageTitle: string;
            matchType?: string;
            fieldsUsed: string[];
          }> | undefined;
          if (!sources || sources.length === 0) return null;
          const grouped = sources.reduce<Record<string, typeof sources>>((acc, s) => {
            (acc[s.database] ??= []).push(s);
            return acc;
          }, {});
          return (
            <details className="text-body-xs" open>
              <summary className="text-text-tertiary cursor-pointer font-medium">
                Notion sources ({sources.length} page{sources.length !== 1 ? 's' : ''} used)
              </summary>
              <div className="mt-2 space-y-3">
                {Object.entries(grouped).map(([db, items]) => (
                  <div key={db}>
                    <p className="text-text-tertiary font-medium mb-1">{db}</p>
                    <ul className="text-text-secondary space-y-1.5">
                      {items.map((s, i) => {
                        const url = s.pageId ? `https://www.notion.so/${s.pageId.replace(/-/g, '')}` : null;
                        return (
                          <li key={i} className="border-l-2 border-border pl-2">
                            <div className="flex items-center gap-2">
                              {url ? (
                                <a href={url} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline font-medium">{s.pageTitle}</a>
                              ) : (
                                <span className="font-medium text-text-primary">{s.pageTitle}</span>
                              )}
                              {s.matchType && <Badge variant="default">{s.matchType}</Badge>}
                            </div>
                            {s.fieldsUsed.length > 0 && (
                              <p className="text-text-tertiary mt-0.5">
                                Fields used: {s.fieldsUsed.join(', ')}
                              </p>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
              </div>
            </details>
          );
        })()}

        {/* Research details */}
        {(() => {
          const research = meta?.research as Record<string, unknown> | undefined;
          if (!research) return null;
          const findings = (research.keyFindings as string[]) ?? [];
          const stats = (research.statistics as Array<{ value: string; source: string }>) ?? [];
          const sources = (research.sources as Array<{ title: string; url: string }>) ?? [];
          return (
            <details className="text-body-xs" open>
              <summary className="text-text-tertiary cursor-pointer font-medium">
                Research ({research.findingsCount as number} findings, {research.sourcesCount as number} sources)
              </summary>
              <div className="mt-2 space-y-3">
                {findings.length > 0 && (
                  <div>
                    <p className="text-text-tertiary font-medium mb-1">Key Findings</p>
                    <ul className="text-text-secondary space-y-1 list-disc list-inside">
                      {findings.map((f, i) => <li key={i}>{f}</li>)}
                    </ul>
                  </div>
                )}
                {stats.length > 0 && (
                  <div>
                    <p className="text-text-tertiary font-medium mb-1">Statistics</p>
                    <ul className="text-text-secondary space-y-1 list-disc list-inside">
                      {stats.map((s, i) => <li key={i}>{s.value} <span className="text-text-tertiary">({s.source})</span></li>)}
                    </ul>
                  </div>
                )}
                {sources.length > 0 && (
                  <div>
                    <p className="text-text-tertiary font-medium mb-1">Sources</p>
                    <ul className="text-text-secondary space-y-1">
                      {sources.map((s, i) => (
                        <li key={i}>
                          <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">{s.title}</a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </details>
          );
        })()}
        {item.seoMetadata && Object.keys(item.seoMetadata).length > 0 && (
          <details className="text-body-xs">
            <summary className="text-text-tertiary cursor-pointer">SEO Metadata</summary>
            <pre className="mt-2 text-text-secondary whitespace-pre-wrap">
              {JSON.stringify(item.seoMetadata, null, 2)}
            </pre>
          </details>
        )}
      </div>
    </Modal>
  );
}

/* ─── Feedback Tab ─── */

const BUILT_IN_CATEGORIES = ['all', 'linkedin_posts', 'seo_content'];

/**
 * `all` is a category: rules that apply to every channel. It used to read "All
 * content" right beside the "All" filter (no filter), which said the same thing
 * for two different choices.
 */
const CATEGORY_LABELS: Record<string, string> = {
  all: 'Every channel',
  linkedin_posts: 'LinkedIn',
  seo_content: 'Website',
};

function categoryLabel(cat: string): string {
  return CATEGORY_LABELS[cat] ?? TYPE_LABELS[cat as ContentType] ?? humanize(cat);
}

/** Content-type slugs inside rule text ("seo_article") read as their labels ("SEO article"). */
const TYPE_SLUG_RE = new RegExp(`\\b(${Object.keys(TYPE_LABELS).join('|')})\\b`, 'g');

function readableRule(text: string): string {
  return text.replace(TYPE_SLUG_RE, (slug) => TYPE_LABELS[slug as ContentType] ?? slug);
}

type RuleEntry<R> = { key: string; rule: R; dupes: R[] };

/**
 * Disabled rules that say nearly the same thing (five "the SEQUESTO OS" rules
 * from repeated edits) fold into one row with "×5". Display only: every rule
 * is still there and can be opened. Active rules are never folded.
 */
function groupNearDuplicates<R extends { id: string; rule: string; isActive: boolean }>(rules: R[]): RuleEntry<R>[] {
  const entries: Array<RuleEntry<R> & { words: Set<string> }> = [];
  for (const r of rules) {
    if (!r.isActive) {
      const words = ruleWords(r.rule);
      const match = entries.find((e) => !e.rule.isActive && wordSimilarity(words, e.words) >= RULE_NEAR_DUPLICATE);
      if (match) {
        match.dupes.push(r);
        continue;
      }
      entries.push({ key: r.id, rule: r, dupes: [], words });
      continue;
    }
    entries.push({ key: r.id, rule: r, dupes: [], words: new Set() });
  }
  return entries.map(({ key, rule, dupes }) => ({ key, rule, dupes }));
}

/** A quiet "Why?" toggle, the same chevron disclosure the rule rows use. */
function WhyDisclosure({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="text-body-xs text-text-tertiary">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="inline-flex items-center gap-1 hover:text-text-secondary"
      >
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden />
        Why?
      </button>
      {open && <p className="mt-1 pl-[1.125rem]">{children}</p>}
    </div>
  );
}

function WebsiteCreateGenerationCard({
  limit,
  pageType,
  selectedSanityType,
  generationResult,
  isGenerating,
  generationError,
  fromStructureSubmitted,
  fromStructureItemId,
  fromStructureGenerateDisabled,
  onGenerate,
  onGoToLibrary,
  disableActions,
}: {
  limit: number | undefined;
  pageType: ContentType | undefined;
  selectedSanityType: string;
  generationResult: BulkGenerateResponse | null;
  isGenerating: boolean;
  generationError: string | null;
  fromStructureSubmitted?: boolean;
  fromStructureItemId?: string | null;
  fromStructureGenerateDisabled?: boolean;
  onGenerate: () => void;
  onGoToLibrary: () => void;
  disableActions: boolean;
}) {
  const expectedDrafts = limit ?? 1;
  const canGenerate = !!pageType;
  const usingFromStructure = !!fromStructureItemId;

  return (
    <div className="space-y-4 border-t border-border pt-4">
      <div className="space-y-1">
        <h3 className="text-body-md font-medium text-text-primary">Generate content</h3>
        <p className="text-body-xs text-text-tertiary">
          {usingFromStructure
            ? 'We’ll fill the empty Sanity draft from step 1 using the brief, run instructions, and Notion context above.'
            : 'We’ll fill the new draft using the structure from step 1 and the configuration above.'}
        </p>
      </div>

      {!canGenerate && (
        <Alert variant="info">
          The Sanity type <code>{selectedSanityType || '—'}</code> doesn’t have a Notion-fed generation path
          configured yet. The blank draft from step 1 has still been created in Sanity: fill it directly in
          Sanity Studio.
        </Alert>
      )}

      <div className="flex flex-wrap items-center gap-4">
        <Button
          onClick={onGenerate}
          disabled={disableActions || !canGenerate || (usingFromStructure && !!fromStructureGenerateDisabled)}
        >
          {isGenerating ? 'Generating...' : 'Generate content'}
        </Button>
        {usingFromStructure ? (
          <p className="text-body-xs text-text-tertiary self-center">
            Generation will run on the existing draft from step 1 (no new Notion row is created).
          </p>
        ) : (
          <p className="text-body-xs text-text-tertiary self-center">
            We’ll generate up to {expectedDrafts} {expectedDrafts === 1 ? 'draft' : 'drafts'} from the current setup.
          </p>
        )}
      </div>

      {generationError && <Alert variant="error">{generationError}</Alert>}

      {usingFromStructure && fromStructureSubmitted && !generationError && (
        <div className="rounded-xl border border-border bg-surface-subtle p-4 space-y-2 text-body-xs">
          <Badge variant="success">Generation started</Badge>
          <p className="text-text-secondary">
            The draft from step 1 is being filled in. Open the Library to follow progress and review the result.
          </p>
          <Button size="sm" variant="secondary" onClick={onGoToLibrary}>
            Go to Library
          </Button>
        </div>
      )}

      {generationResult && (
        <div className="space-y-3 rounded-xl border border-border bg-surface-subtle p-4 text-body-xs">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="success">
              {generationResult.scheduled ?? generationResult.items?.length ?? 0} ready
            </Badge>
            {pageType && <Badge variant="default">{TYPE_LABELS[pageType] ?? pageType}</Badge>}
            {generationResult.failed !== undefined && generationResult.failed > 0 && (
              <Badge variant="error">{generationResult.failed} failed</Badge>
            )}
          </div>

          <p className="text-text-secondary">
            Matching rows: {generationResult.total}
            {generationResult.scheduled !== undefined && ` · drafts ready for review: ${generationResult.scheduled}`}
          </p>

          <div className="rounded-xl border border-border bg-surface-primary px-4 py-4 space-y-3">
            <div className="text-text-secondary">
              The generated drafts are now available in the Library, where they can be reopened, reviewed, and pushed to Sanity when ready.
            </div>
            <Button size="sm" variant="secondary" onClick={onGoToLibrary}>
              Go to Library
            </Button>
          </div>

          {generationResult.failures && generationResult.failures.length > 0 && (
            <div className="space-y-1 pt-1">
              <p className="font-medium text-error-dark">Failures</p>
              {generationResult.failures.map((failure) => (
                <p key={failure.title} className="text-error-dark">
                  {failure.title}: {failure.error}
                </p>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** On/off switch for a rule: ink when on, a quiet track when off. Not a status colour. */
function RuleToggle({ active, onToggle }: { active: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={active}
      onClick={onToggle}
      title={active ? 'Disable' : 'Enable'}
      className={`relative h-5 w-9 flex-none rounded-full transition-colors ${active ? 'bg-text-primary' : 'bg-border'}`}
    >
      <span
        className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${
          active ? 'left-[18px]' : 'left-0.5'
        }`}
      />
    </button>
  );
}

/**
 * Type and status said once above a card grid when every card shares them,
 * instead of the same two pills on every card.
 */
function SharedPills({ items, block = false }: { items: ContentItem[]; block?: boolean }) {
  const parts = [
    isConstant(items, (x) => x.type) && items[0] ? TYPE_LABELS[items[0].type] ?? items[0].type : null,
    isConstant(items, getDisplayStatusLabel) && items[0] ? getDisplayStatusLabel(items[0]) : null,
  ].filter(Boolean);
  if (parts.length === 0) return null;
  const text = `All ${parts.join(' · ')}`;
  return block ? (
    <p className="text-body-xs text-text-tertiary">{text}</p>
  ) : (
    <span className="ml-2 font-normal text-body-xs text-text-tertiary">{text}</span>
  );
}

function PendingRulesPanel() {
  const { data, isLoading, isError, error } = usePendingFeedbackRules();
  const patchRule = usePatchFeedbackRule();
  const deleteRule = useDeleteFeedbackRule();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');

  if (isLoading) return null;
  if (isError) {
    return <ErrorState title="Couldn’t load pending rules" error={error} />;
  }
  const rules = data?.rules ?? [];
  if (rules.length === 0) return null;

  return (
    <Card>
      <div className="space-y-3">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-body-sm font-medium text-text-primary">
              Pending rules from your edits ({rules.length})
            </p>
            <p className="text-body-xs text-text-tertiary">
              Auto-extracted from manual edits in the library. Approve to start influencing future
              generations.
            </p>
          </div>
        </div>
        {/* A divided list, not bordered boxes: boxes inside the card doubled every border. */}
        <ul className="divide-y divide-border-light border-t border-border-light">
          {rules.map((r) => (
            <li key={r.id} className="py-3 space-y-2">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 space-y-1">
                  {editingId === r.id ? (
                    <Textarea
                      rows={3}
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                    />
                  ) : (
                    <p className="text-body-sm text-text-primary">{readableRule(r.rule)}</p>
                  )}
                  <div className="flex flex-wrap gap-2 text-body-xs text-text-tertiary">
                    <Badge>{categoryLabel(r.category)}</Badge>
                    {r.sourceItemTitle && (
                      <span className="italic">from "{r.sourceItemTitle}"</span>
                    )}
                  </div>
                  {r.diffSummary && <WhyDisclosure>{readableRule(r.diffSummary)}</WhyDisclosure>}
                </div>
                <div className="flex flex-none flex-wrap items-center gap-1">
                  {editingId === r.id ? (
                    <>
                      <Button
                        size="sm"
                        variant="primary"
                        disabled={!editText.trim() || patchRule.isPending}
                        onClick={() =>
                          patchRule.mutate(
                            { id: r.id, rule: editText.trim(), status: 'active' },
                            { onSuccess: () => setEditingId(null) },
                          )
                        }
                      >
                        Save & approve
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        size="sm"
                        variant="primary"
                        disabled={patchRule.isPending}
                        onClick={() => patchRule.mutate({ id: r.id, status: 'active' })}
                      >
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => {
                          setEditingId(r.id);
                          setEditText(r.rule);
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => {
                          if (!confirm('Reject this rule? It will not influence generation.')) return;
                          deleteRule.mutate(r.id);
                        }}
                      >
                        Reject
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </Card>
  );
}

function ApplyRuleToLibraryModal({
  ruleId,
  ruleText,
  onClose,
}: {
  ruleId: string;
  ruleText: string;
  onClose: () => void;
}) {
  const previewQuery = useRuleLibraryPreview(ruleId, true);
  const applyMutation = useApplyRuleToLibrary();
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const [result, setResult] = useState<RuleApplicationResult | null>(null);

  // Default-exclude items in `review` status — they're being actively looked
  // at by reviewers, so don't change the copy out from under them. The user
  // can re-include them with a click.
  useEffect(() => {
    if (!previewQuery.data || !('supported' in previewQuery.data) || !previewQuery.data.supported) return;
    const next = new Set<string>();
    for (const c of previewQuery.data.candidates) {
      if (c.status === 'review') next.add(c.id);
    }
    setExcluded(next);
  }, [previewQuery.data]);

  function toggle(id: string) {
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function handleApply() {
    const res = await applyMutation.mutateAsync({
      ruleId,
      excludeContentItemIds: [...excluded],
    });
    setResult(res);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <Card className="w-full max-w-2xl max-h-[85vh] overflow-y-auto p-5 space-y-4">
        <div className="space-y-1">
          <h3 className="text-body-md font-medium text-text-primary">Apply rule to library</h3>
          <p className="text-body-xs text-text-tertiary">"{ruleText}"</p>
        </div>

        {previewQuery.isLoading && (
          <div className="flex items-center gap-2 text-body-xs text-text-secondary">
            <Spinner /> Scanning library…
          </div>
        )}

        {previewQuery.data && 'supported' in previewQuery.data && !previewQuery.data.supported && (
          <Alert variant="warning">{previewQuery.data.reason}</Alert>
        )}

        {previewQuery.data && 'supported' in previewQuery.data && previewQuery.data.supported && !result && (
          <>
            <p className="text-body-sm text-text-secondary">
              {previewQuery.data.description} {previewQuery.data.candidates.length === 0
                ? 'No library drafts contain matches for this rule.'
                : `${previewQuery.data.candidates.length} draft${previewQuery.data.candidates.length === 1 ? '' : 's'} would be touched (${previewQuery.data.totalReplacements} replacement${previewQuery.data.totalReplacements === 1 ? '' : 's'} total).`}
            </p>
            {previewQuery.data.candidates.length > 0 && (
              <div className="space-y-2 max-h-96 overflow-y-auto rounded-md border border-border p-2">
                {previewQuery.data.candidates.map((c: RuleLibraryCandidate) => {
                  const isExcluded = excluded.has(c.id);
                  return (
                    <label
                      key={c.id}
                      className="flex items-center gap-3 px-2 py-1.5 rounded hover:bg-surface-secondary cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={!isExcluded}
                        onChange={() => toggle(c.id)}
                        className="h-4 w-4 cursor-pointer rounded-sm border-border"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="text-body-sm text-text-primary truncate">{c.title}</div>
                        <div className="text-body-xs text-text-tertiary">
                          {c.status === 'review' ? 'In review' : 'Draft'} · {c.replacements} match{c.replacements === 1 ? '' : 'es'}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
            {applyMutation.error instanceof Error && (
              <Alert variant="error">{applyMutation.error.message}</Alert>
            )}
            <div className="flex justify-end gap-2 pt-2 border-t border-border">
              <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => { void handleApply(); }}
                disabled={
                  previewQuery.data.candidates.length === 0 ||
                  applyMutation.isPending ||
                  previewQuery.data.candidates.every((c) => excluded.has(c.id))
                }
                isLoading={applyMutation.isPending}
              >
                Apply to {previewQuery.data.candidates.filter((c) => !excluded.has(c.id)).length} draft{previewQuery.data.candidates.filter((c) => !excluded.has(c.id)).length === 1 ? '' : 's'}
              </Button>
            </div>
          </>
        )}

        {result && (
          <>
            <Alert variant="success">
              Applied "{result.description}": {result.touched.length} touched, {result.skipped.length} skipped, {result.failed.length} failed.
            </Alert>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {result.touched.length > 0 && (
                <div>
                  <div className="text-body-xs font-medium text-text-secondary uppercase tracking-wide mb-1">Touched</div>
                  <ul className="space-y-1">
                    {result.touched.map((t) => (
                      <li key={t.id} className="text-body-sm text-text-primary">
                        ✅ {t.title}: {t.replacements} replacement{t.replacements === 1 ? '' : 's'} (v{t.versionNum})
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {result.skipped.length > 0 && (
                <div>
                  <div className="text-body-xs font-medium text-text-secondary uppercase tracking-wide mb-1">Skipped</div>
                  <ul className="space-y-1">
                    {result.skipped.map((s) => (
                      <li key={s.id} className="text-body-sm text-text-secondary">
                        {s.title} ({s.reason})
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {result.failed.length > 0 && (
                <div>
                  <div className="text-body-xs font-medium text-error uppercase tracking-wide mb-1">Failed</div>
                  <ul className="space-y-1">
                    {result.failed.map((f) => (
                      <li key={f.id} className="text-body-sm text-error">
                        ❌ {f.title}: {f.reason}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div className="flex justify-end pt-2 border-t border-border">
              <Button variant="primary" size="sm" onClick={onClose}>Done</Button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}

const ISSUE_TITLES: Record<RuleIssue['kind'], string> = {
  'em-dash': 'Uses an em dash another rule bans',
  'product-name': 'Spells the product differently',
  'opposite-wording': 'Asks for the opposite wording',
  'near-duplicate': 'Repeats another rule',
};

/** The same kinds in a few words, for the panel's one-line summary. */
const ISSUE_SHORT: Record<RuleIssue['kind'], string> = {
  'em-dash': 'em dash',
  'product-name': 'product name',
  'opposite-wording': 'opposite wording',
  'near-duplicate': 'repeat',
};

/**
 * Active rules that break another active rule or repeat one. Advice only: the
 * page never edits a rule; open it and fix the wording by hand.
 */
function RuleIssuesPanel({
  issues,
  rules,
  error,
  onOpen,
}: {
  issues: RuleIssue[];
  rules: Array<{ id: string; rule: string; priority: number }>;
  error: unknown;
  onOpen: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  if (error) return <Alert variant="warning">Couldn’t check rules for conflicts: the full rule list didn’t load.</Alert>;
  if (issues.length === 0) return null;
  const byId = new Map(rules.map((r) => [r.id, r]));
  const kinds = (Object.keys(ISSUE_TITLES) as RuleIssue['kind'][]).filter((k) => issues.some((i) => i.kind === k));
  // Both sides of every issue, once each: a rule in two issues was counted twice.
  const ruleCount = issueRuleIds(issues).size;
  return (
    <Card>
      <div className="space-y-3">
        {/* One line by default; the list opens on demand. */}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="flex w-full items-center gap-2 text-left"
        >
          <ChevronDown className={`h-4 w-4 flex-none text-text-tertiary transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden />
          <span className="text-body-md font-medium text-text-primary">
            {issues.length} {issues.length === 1 ? 'problem' : 'problems'} to fix across {ruleCount} active {ruleCount === 1 ? 'rule' : 'rules'}
          </span>
          <span className="text-body-xs text-text-tertiary">
            · {kinds.map((k) => `${ISSUE_SHORT[k]} ${issues.filter((i) => i.kind === k).length}`).join(' · ')}
          </span>
        </button>
        {open && (
          <p className="text-body-xs text-text-tertiary">
            Every active rule goes into every prompt, so a draft can’t follow both. Open a rule to fix its wording.
          </p>
        )}
        {open && kinds.map((kind) => (
          <div key={kind} className="space-y-1">
            <p className="text-body-xs font-medium text-text-secondary">
              {ISSUE_TITLES[kind]} ({issues.filter((i) => i.kind === kind).length})
            </p>
            <ul className="divide-y divide-border-light">
              {issues
                .filter((i) => i.kind === kind)
                .map((i) => {
                  const r = byId.get(i.ruleId);
                  return (
                    <li key={`${i.ruleId}-${i.otherId}-${kind}`} className="py-1.5">
                      <button
                        type="button"
                        onClick={() => onOpen(i.ruleId)}
                        className="block w-full text-left text-body-sm text-text-primary hover:text-accent"
                      >
                        {r ? `P${r.priority} · ${readableRule(r.rule)}` : i.ruleId}
                      </button>
                      <p className="text-body-xs text-text-tertiary">{i.message}</p>
                    </li>
                  );
                })}
            </ul>
          </div>
        ))}
      </div>
    </Card>
  );
}

function FeedbackTab() {
  const [filterCategory, setFilterCategory] = useState<string | undefined>(undefined);
  const rulesQuery = useFeedbackRules(filterCategory ? { category: filterCategory } : undefined);
  const { data, isLoading } = rulesQuery;
  const [rulesOpen, setRulesOpen] = useState(false);
  const createRule = useCreateFeedbackRule();
  const patchRule = usePatchFeedbackRule();
  const deleteRule = useDeleteFeedbackRule();

  const [showForm, setShowForm] = useState(false);
  const [newRule, setNewRule] = useState('');
  const [newCategory, setNewCategory] = useState('all');
  const [newPriority, setNewPriority] = useState(0);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [editPriority, setEditPriority] = useState(0);
  const [applyRuleId, setApplyRuleId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Conflicts are checked across every category: the "no em dash" rule applies to every channel.
  const allRulesQuery = useFeedbackRules();
  const [hideDisabled, setHideDisabled] = useState(true);

  // Pending rules have their own panel above; listing them here too showed them as switched off.
  const rules = useMemo(() => (data?.rules ?? []).filter((r) => r.status !== 'pending'), [data]);
  const activeCount = rules.filter((r) => r.isActive).length;
  const issues = useMemo(() => findRuleIssues(allRulesQuery.data?.rules ?? []), [allRulesQuery.data]);
  // Both sides of a conflict get the badge: the em-dash ban (P10) and the brand-name reference (P30) had none.
  const issuesByRule = useMemo(() => issuesPerRule(issues), [issues]);
  const shown = useMemo(() => (hideDisabled ? rules.filter((r) => r.isActive) : rules), [rules, hideDisabled]);
  const entries = useMemo(() => groupNearDuplicates(shown), [shown]);
  const folded = shown.length - entries.length;
  const rulePage = usePagination(entries, 15);

  const allCategories = [...new Set([...BUILT_IN_CATEGORIES, ...rules.map((r) => r.category)])];

  const handleCreate = () => {
    if (!newRule.trim()) return;
    createRule.mutate(
      { rule: newRule.trim(), category: newCategory, priority: newPriority },
      {
        onSuccess: () => {
          setNewRule('');
          setNewCategory('all');
          setNewPriority(0);
          setShowForm(false);
        },
      },
    );
  };

  const handleToggle = (id: string, isActive: boolean) => {
    patchRule.mutate({ id, isActive: !isActive });
  };

  const handleDelete = (id: string) => {
    if (!confirm('Delete this generation rule?')) return;
    deleteRule.mutate(id);
  };

  const handleEditSave = (id: string) => {
    if (!editText.trim()) return;
    patchRule.mutate(
      { id, rule: editText.trim(), priority: editPriority },
      { onSuccess: () => setEditingId(null) },
    );
  };

  if (rulesQuery.error) {
    return <ErrorState title="Couldn’t load generation rules" error={rulesQuery.error} onRetry={() => rulesQuery.refetch()} />;
  }
  if (isLoading) {
    return <LoadingState rows={4} />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <p className="text-body-sm text-text-tertiary">
            Editorial rules injected into every content generation prompt. {activeCount} of {rules.length} active.
          </p>
        </div>
        <Button variant="primary" size="sm" onClick={() => setShowForm(!showForm)}>
          {showForm ? 'Cancel' : 'Add rule'}
        </Button>
      </div>

      {/* Add Rule Form */}
      {showForm && (
        <Card>
          <div className="space-y-4">
            <div>
              <label className="mb-1 block text-body-sm font-medium text-text-secondary">Rule</label>
              <Textarea
                rows={3}
                placeholder="e.g. Never use rhetorical questions as hooks in LinkedIn posts"
                value={newRule}
                onChange={(e) => setNewRule(e.target.value)}
              />
            </div>
            <div className="flex gap-4">
              <div className="flex-1">
                <label className="mb-1 block text-body-sm font-medium text-text-secondary">Applies to</label>
                <Select value={newCategory} onChange={(e) => setNewCategory(e.target.value)} className="w-full">
                  {allCategories.map((cat) => (
                    <option key={cat} value={cat}>
                      {categoryLabel(cat)}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="w-32">
                <label className="mb-1 block text-body-sm font-medium text-text-secondary">Priority</label>
                <Input
                  type="number"
                  min={0}
                  max={100}
                  value={newPriority}
                  onChange={(e) => setNewPriority(Number(e.target.value))}
                />
              </div>
            </div>
            <div className="flex justify-end">
              <Button variant="primary" size="sm" onClick={handleCreate} isLoading={createRule.isPending}>
                Save rule
              </Button>
            </div>
          </div>
        </Card>
      )}

      <PendingRulesPanel />

      <RuleIssuesPanel
        issues={issues}
        rules={allRulesQuery.data?.rules ?? []}
        error={allRulesQuery.error}
        onOpen={(id) => {
          setHideDisabled(false);
          setFilterCategory(undefined);
          setRulesOpen(true);
          setExpandedId(id);
        }}
      />

      {/* Rules: long list, so it starts collapsed behind its count. */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => setRulesOpen((v) => !v)}
          aria-expanded={rulesOpen}
          className="flex items-center gap-2 text-body-md font-medium text-text-primary"
        >
          <ChevronDown className={`h-4 w-4 text-text-tertiary transition-transform ${rulesOpen ? 'rotate-180' : ''}`} aria-hidden />
          {filterCategory ? `${categoryLabel(filterCategory)} rules` : 'All rules'} ({shown.length})
          {folded > 0 && (
            <span className="text-body-xs font-normal text-text-tertiary">· {folded} near-duplicate disabled {folded === 1 ? 'rule' : 'rules'} folded</span>
          )}
        </button>
        <label className="flex items-center gap-2 text-body-xs text-text-secondary">
          <input
            type="checkbox"
            checked={hideDisabled}
            onChange={(e) => {
              setHideDisabled(e.target.checked);
              setRulesOpen(true);
            }}
          />
          Hide disabled ({rules.length - activeCount})
        </label>
        <SegmentedControl
          ariaLabel="Rule category"
          options={[{ id: '__all', label: 'All rules' }, ...allCategories.map((cat) => ({ id: cat, label: categoryLabel(cat) }))]}
          value={filterCategory ?? '__all'}
          onChange={(id) => {
            setFilterCategory(id === '__all' ? undefined : id);
            setRulesOpen(true);
          }}
        />
      </div>

      {/* Rules List: one line per rule, opened for editing and actions. */}
      {!rulesOpen ? null : shown.length === 0 ? (
        <Card>
          <EmptyState compact title="No generation rules yet" description="Add your first rule to start shaping content generation." />
        </Card>
      ) : (
        <Card padding="none">
          <ul className="divide-y divide-border-light">
            {rulePage.pageItems.map(({ rule, dupes }) => {
              const open = expandedId === rule.id || editingId === rule.id;
              return (
                <li key={rule.id} className="px-4 py-2.5">
                  <div className="flex items-center gap-3">
                    <RuleToggle active={rule.isActive} onToggle={() => handleToggle(rule.id, rule.isActive)} />
                    <button
                      type="button"
                      onClick={() => setExpandedId(open ? null : rule.id)}
                      aria-expanded={open}
                      title={open ? undefined : readableRule(rule.rule)}
                      className={`min-w-0 flex-1 text-left text-body-sm ${open ? 'whitespace-normal' : 'truncate'} ${
                        rule.isActive ? 'text-text-primary' : 'text-text-tertiary'
                      }`}
                    >
                      {editingId === rule.id ? 'Editing rule' : readableRule(rule.rule)}
                    </button>
                    {(issuesByRule.get(rule.id)?.length ?? 0) > 0 && (
                      <span className="flex-none" title={issuesByRule.get(rule.id)!.map((i) => i.message).join('\n')}>
                        <Badge variant="warning">Conflict</Badge>
                      </span>
                    )}
                    {dupes.length > 0 && (
                      <span
                        className="flex-none text-body-xs tabular-nums text-text-tertiary"
                        title={`${dupes.length + 1} disabled rules say nearly the same thing; open to see them`}
                      >
                        ×{dupes.length + 1}
                      </span>
                    )}
                    <span className="hidden flex-none sm:inline-flex">
                      <Badge>{categoryLabel(rule.category)}</Badge>
                    </span>
                    {rule.priority > 0 && (
                      <span className="flex-none text-body-xs tabular-nums text-text-tertiary" title="Priority">
                        P{rule.priority}
                      </span>
                    )}
                    <ChevronDown
                      className={`h-4 w-4 flex-none text-text-tertiary transition-transform ${open ? 'rotate-180' : ''}`}
                      aria-hidden
                    />
                  </div>
                  {open && (
                    <div className="mt-3 space-y-3 pl-12">
                      {editingId !== rule.id &&
                        issuesByRule.get(rule.id)?.map((i) => (
                          <p key={`${i.kind}-${i.otherId}`} className="text-body-xs text-warning-dark">
                            {i.message}
                          </p>
                        ))}
                      {dupes.length > 0 && editingId !== rule.id && (
                        <div className="space-y-1 text-body-xs text-text-tertiary">
                          <p>Also disabled, nearly the same:</p>
                          <ul className="list-disc space-y-0.5 pl-4">
                            {dupes.map((d) => (
                              <li key={d.id}>{readableRule(d.rule)}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {editingId === rule.id ? (
                        <div className="space-y-2">
                          <Textarea rows={3} value={editText} onChange={(e) => setEditText(e.target.value)} />
                          <div className="w-32">
                            <label className="mb-1 block text-body-xs font-medium text-text-secondary">Priority</label>
                            <Input
                              type="number"
                              min={0}
                              max={100}
                              value={editPriority}
                              onChange={(e) => setEditPriority(Number(e.target.value))}
                            />
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" variant="primary" onClick={() => handleEditSave(rule.id)} isLoading={patchRule.isPending}>
                              Save
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="sm:hidden">
                            <Badge>{categoryLabel(rule.category)}</Badge>
                          </span>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => setApplyRuleId(rule.id)}
                            title="Apply this rule to existing library drafts"
                          >
                            Apply to library
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setEditingId(rule.id);
                              setEditText(rule.rule);
                              setEditPriority(rule.priority);
                            }}
                          >
                            Edit
                          </Button>
                          <button
                            type="button"
                            onClick={() => handleDelete(rule.id)}
                            title="Delete rule"
                            aria-label="Delete rule"
                            className="ml-auto flex h-8 w-8 items-center justify-center rounded text-text-tertiary transition-colors hover:bg-error-light hover:text-error-dark"
                          >
                            <Trash2 className="h-4 w-4" aria-hidden />
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </Card>
      )}

      {rulesOpen && <Pagination {...rulePage} onPage={rulePage.setPage} />}

      {applyRuleId && (() => {
        const ruleForApply = rules.find((r) => r.id === applyRuleId);
        if (!ruleForApply) return null;
        return (
          <ApplyRuleToLibraryModal
            ruleId={applyRuleId}
            ruleText={ruleForApply.rule}
            onClose={() => setApplyRuleId(null)}
          />
        );
      })()}
    </div>
  );
}
